const MAX_CREATIVE_CREDITS_LENGTH = 1000;
const DASHBOARD_CART_SPLIT_THRESHOLD_PEN = 0;

const CREATIVE_CREDITS_PLACEHOLDER = `- Director General Creativo: 
- Director General de Arte:
- Director General de Marcas:
- Etc.`;

const LEGACY_CREDIT_FIELDS = [
  { key: 'creativeDirector', label: 'Director creativo' },
  { key: 'copywriter', label: 'Redactor' },
  { key: 'artDirector', label: 'Director de arte' },
  { key: 'producer', label: 'Productor / Productora' },
  { key: 'director', label: 'Director' },
  { key: 'other', label: 'Otros roles' },
];

function parseCreativeCreditsInput(value) {
  return String(value ?? '').trim().slice(0, MAX_CREATIVE_CREDITS_LENGTH);
}

function isSubmissionSuccessCard(status) {
  return status && status !== 'DRAFT' && status !== 'OBSERVED';
}

function resolveCreativeCredits(submission) {
  const direct = submission?.creativeCredits?.trim();
  if (direct) return direct.slice(0, MAX_CREATIVE_CREDITS_LENGTH);

  const credits = submission?.credits;
  if (!credits || typeof credits !== 'object') return '';

  if (typeof credits.creativeCredits === 'string' && credits.creativeCredits.trim()) {
    return credits.creativeCredits.trim().slice(0, MAX_CREATIVE_CREDITS_LENGTH);
  }

  return LEGACY_CREDIT_FIELDS.map((field) => {
    const value = credits[field.key]?.trim();
    return value ? `${field.label}: ${value}` : null;
  })
    .filter(Boolean)
    .join('\n')
    .slice(0, MAX_CREATIVE_CREDITS_LENGTH);
}

function resolveCaseTypeFromCategoryIds(categoryIds, categories) {
  if (!categoryIds?.length || !categories?.length) return '';
  const selected = categories.filter((category) => categoryIds.includes(category.id));
  if (!selected.length) return '';
  const types = [...new Set(selected.map((category) => category.caseType || 'PIEZA'))];
  return types.length === 1 ? types[0] : '';
}

function categoryMatchesActiveCaseType(category, activeCaseType) {
  const type = category.caseType || 'PIEZA';
  return !activeCaseType || type === activeCaseType;
}

const WIZARD_STEPS = [
  { id: 1, label: 'Categorías' },
  { id: 2, label: 'Datos del caso' },
  { id: 3, label: 'Medios' },
  { id: 4, label: 'Revisión' },
];

const STEP_INTROS = {
  1: {
    title: 'Categorías',
    lead: 'Selecciona las categorías de concurso para tu postulación.',
  },
  2: {
    title: 'Datos del caso',
    lead: 'Completa la información general, los datos del participante y del anunciante.',
  },
  3: {
    title: 'Medios',
    lead: 'Adjunta archivos o enlaces que respalden tu postulación.',
  },
  4: {
    title: 'Revisión',
    lead: 'Verifica que todo esté completo antes de enviar.',
  },
};

const AUTOSAVE_MS = 60_000;
const SAVE_DEBOUNCE_MS = 500;
const DEFAULT_MAX_CATEGORIES = 5;

const ACCEPT_TERMS_FIELD = 'acceptTerms';
const ACCEPT_PRIVACY_FIELD = 'acceptPrivacy';
const LEGAL_ACCEPTANCE_ERROR_MESSAGE =
  'Debes aceptar los términos, las bases del premio y las políticas de privacidad para enviar.';

function isValidRuc(value) {
  return /^\d{11}$/.test(String(value ?? '').trim());
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value ?? '').trim());
}

const DEBUG_PERF =
  typeof window !== 'undefined' &&
  (window.__APAP_DEBUG_PERF__ ||
    new URLSearchParams(window.location.search).get('debug_perf') === '1');

function debounce(fn, wait) {
  let timer = null;
  return function debounced(...args) {
    if (timer) window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      timer = null;
      fn.apply(this, args);
    }, wait);
  };
}

function perfStep(label, fn) {
  if (!DEBUG_PERF) return fn();
  const start = performance.now();
  return Promise.resolve(fn()).finally(() => {
    console.log(`[apap-perf] ${label}: ${(performance.now() - start).toFixed(1)}ms`);
  });
}

/** Activar subida de archivos de video en el theme (el API ya lo soporta). */
const ENABLE_VIDEO_FILE_UPLOAD = true;

/**
 * El countdown público se muestra 1h antes del cierre real del Admin.
 * Así se puede alargar el cierre operativo 1h sin que el reloj de cara al usuario se mueva.
 */
const COUNTDOWN_DISPLAY_OFFSET_MS = -60 * 60 * 1000;

/** Mostrar compra de inscripciones en la ficha/wizard (el dashboard siempre la muestra). */
const ENABLE_QUOTA_PAYMENT_UI = false;

const MAX_IMAGE_PIXELS = 5000;

const MEDIA_UPLOAD_LIMITS = {
  IMAGE: {
    maxBytes: 20 * 1024 * 1024,
    maxPixels: MAX_IMAGE_PIXELS,
    accept: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
    label: '20 MB',
    dimensionLabel: '5000×5000 px',
  },
  AUDIO: {
    maxBytes: 50 * 1024 * 1024,
    accept: ['audio/mpeg', 'audio/wav', 'audio/mp3', 'audio/x-wav'],
    label: '50 MB',
  },
  PDF: {
    maxBytes: 20 * 1024 * 1024,
    accept: ['application/pdf'],
    label: '20 MB',
  },
  VIDEO: {
    maxBytes: 400 * 1024 * 1024,
    accept: ['video/mp4', 'video/quicktime'],
    label: '400 MB',
  },
};

/** Detección de códec MP4/MOV (fourcc). Solo se permiten videos H.264. */
const VIDEO_FOURCC = {
  avc1: [0x61, 0x76, 0x63, 0x31],
  avc3: [0x61, 0x76, 0x63, 0x33],
  av01: [0x61, 0x76, 0x30, 0x31],
  hev1: [0x68, 0x65, 0x76, 0x31],
  hvc1: [0x68, 0x76, 0x63, 0x31],
};

function includesFourcc(haystack, needle) {
  outer: for (let i = 0; i <= haystack.length - needle.length; i += 1) {
    for (let j = 0; j < needle.length; j += 1) {
      if (haystack[i + j] !== needle[j]) continue outer;
    }
    return true;
  }
  return false;
}

function detectMp4VideoCodec(data) {
  if (includesFourcc(data, VIDEO_FOURCC.av01)) return 'av1';
  if (includesFourcc(data, VIDEO_FOURCC.hev1) || includesFourcc(data, VIDEO_FOURCC.hvc1)) {
    return 'hevc';
  }
  if (includesFourcc(data, VIDEO_FOURCC.avc1) || includesFourcc(data, VIDEO_FOURCC.avc3)) {
    return 'h264';
  }
  return 'unknown';
}

function shopifyVideoUploadErrorMessage(codec, fileName) {
  const name = fileName ? ` "${fileName}"` : '';
  return (
    `El formato del video${name} no está permitido. ` +
    'Solo se permiten MP4 o MOV en H.264 (AVC) con audio AAC.'
  );
}

async function assertShopifyCompatibleVideoFile(file) {
  const PROBE = 8 * 1024 * 1024;
  let bytes;
  if (file.size <= PROBE * 2) {
    bytes = new Uint8Array(await file.arrayBuffer());
  } else {
    const head = new Uint8Array(await file.slice(0, PROBE).arrayBuffer());
    const tail = new Uint8Array(await file.slice(file.size - PROBE).arrayBuffer());
    bytes = new Uint8Array(head.length + tail.length);
    bytes.set(head, 0);
    bytes.set(tail, head.length);
  }
  const codec = detectMp4VideoCodec(bytes);
  if (codec !== 'h264') {
    throw new Error(shopifyVideoUploadErrorMessage(codec, file.name));
  }
  return codec;
}

class ThemeApiClient {
  /** @param {{ apiBase: string; uploadBase: string }} config */
  constructor(config) {
    this.apiBase = config.apiBase.replace(/\/$/, '');
    this.uploadBase = config.uploadBase.replace(/\/$/, '');
    this.impersonateEmail = '';
  }

  impersonateHeaders(extra = {}) {
    return {
      ...extra,
      ...(this.impersonateEmail ? { 'X-Apap-As': this.impersonateEmail } : {}),
    };
  }

  resolveMediaUrl(path) {
    if (!path) return path;
    if (path.startsWith('http')) return path;
    if (path.startsWith('/apps/')) return path;
    if (path.startsWith('/proxy/')) {
      const prefix = this.uploadBase.replace(/\/api$/, '');
      return `${prefix}${path}`;
    }
    if (path.startsWith('/api/')) return `${this.uploadBase}${path.replace(/^\/api/, '')}`;
    return path;
  }

  async request(path, options = {}) {
    const url = `${this.apiBase}${path.startsWith('/') ? path : `/${path}`}`;
    const response = await fetch(url, {
      credentials: 'same-origin',
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...options.headers,
        ...(this.impersonateEmail
          ? { 'X-Apap-As': this.impersonateEmail }
          : {}),
      },
    });
    const payload = await response.json().catch(() => null);
    if (!payload) {
      if (!response.ok) {
        throw new Error(`Error del servidor (${response.status}). Intenta de nuevo.`);
      }
      throw new Error('Respuesta inválida del servidor.');
    }
    if (!payload.ok) {
      const message = payload.error?.message ?? 'Error desconocido';
      const error = new Error(message);
      error.code = payload.error?.code;
      throw error;
    }
    return payload.data;
  }

  bootstrap(body = {}) {
    return this.request('/session/bootstrap', {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    });
  }

  me() {
    return this.request('/session/me');
  }

  lookupImpersonate(email) {
    return this.request(`/impersonate/lookup?email=${encodeURIComponent(email)}`);
  }

  saveProfile(body) {
    return this.request('/profile', { method: 'PUT', body: JSON.stringify(body) });
  }

  partnerAgencies() {
    return this.request('/partner-agencies');
  }

  dashboard() {
    return this.request('/dashboard');
  }

  createSubmission() {
    return this.request('/submissions', { method: 'POST', body: '{}' });
  }

  getSubmission(id) {
    return this.request(`/submissions/${encodeURIComponent(id)}`);
  }

  updateSubmission(id, body) {
    return this.request(`/submissions/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  submitSubmission(id, body) {
    return this.request(`/submissions/${encodeURIComponent(id)}/submit`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  deleteSubmission(id) {
    return this.request(`/submissions/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  editionCatalog() {
    return this.request('/catalog/edition');
  }

  comprarCatalog() {
    return this.request('/comprar/catalog');
  }

  comprarCheckout(body) {
    return this.request('/comprar/checkout', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  dashboardCheckout() {
    return this.request('/dashboard/checkout', {
      method: 'POST',
      body: '{}',
    });
  }

  async uploadFile(submissionId, mediaType, file, onProgress) {
    if (onProgress) onProgress(0);

    const initRes = await fetch(`${this.uploadBase}/upload`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: this.impersonateHeaders({ 'Content-Type': 'application/json', Accept: 'application/json' }),
      body: JSON.stringify({
        submissionId,
        fileName: file.name,
        mimeType: file.type,
        fileSize: file.size,
        mediaType,
      }),
    });
    const initPayload = await initRes.json().catch(() => null);
    if (!initRes.ok || !initPayload) {
      throw new Error(this.parseUploadError(initPayload, initRes.status));
    }
    if (initPayload.ok === false) {
      throw new Error(this.parseUploadError(initPayload, initRes.status));
    }

    let uploadUrl = initPayload.uploadUrl;
    if (!uploadUrl && initPayload.data?.uploadUrl) {
      uploadUrl = initPayload.data.uploadUrl;
    }
    if (!uploadUrl) {
      throw new Error('No se recibió URL de subida.');
    }
    if (uploadUrl?.startsWith('/proxy/')) {
      uploadUrl = `${this.uploadBase.replace(/\/api$/, '')}${uploadUrl}`;
    } else if (uploadUrl?.startsWith('/api/')) {
      uploadUrl = `${this.uploadBase}${uploadUrl.replace(/^\/api/, '')}`;
    }

    const assetId = initPayload.assetId ?? initPayload.data?.assetId;
    const fileKey = initPayload.fileKey ?? initPayload.data?.fileKey;
    const directUploadUrl = initPayload.directUploadUrl ?? initPayload.data?.directUploadUrl;
    const confirmUrl = this.resolveUploadApiUrl(
      initPayload.confirmUrl ?? initPayload.data?.confirmUrl ?? '/api/upload/confirm',
    );
    const abortUrl = this.resolveUploadApiUrl(
      initPayload.abortUrl ?? initPayload.data?.abortUrl ?? '/api/upload/abort',
    );

    try {
      if (directUploadUrl) {
        if (onProgress) {
          await this.uploadDirectWithProgress(directUploadUrl, file, onProgress);
        } else {
          const putRes = await fetch(directUploadUrl, {
            method: 'PUT',
            body: await file.arrayBuffer(),
          });
          if (!putRes.ok) throw new Error('Error al subir archivo.');
        }
        const confirmRes = await fetch(confirmUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: this.impersonateHeaders({ 'Content-Type': 'application/json', Accept: 'application/json' }),
          body: JSON.stringify({ assetId, fileKey }),
        });
        if (!confirmRes.ok) {
          const err = await confirmRes.json().catch(() => null);
          throw new Error(this.parseUploadError(err, confirmRes.status));
        }
      } else {
        const form = new FormData();
        form.append('file', file);

        if (onProgress) {
          await this.uploadWithProgress(uploadUrl, form, onProgress);
        } else {
          const uploadRes = await fetch(uploadUrl, {
            method: 'POST',
            credentials: 'same-origin',
            body: form,
          });
          if (!uploadRes.ok) {
            const err = await uploadRes.json().catch(() => null);
            throw new Error(this.parseUploadError(err, uploadRes.status));
          }
        }
      }
    } catch (error) {
      if (assetId) {
        await fetch(abortUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: this.impersonateHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ assetId }),
        }).catch(() => {});
      }
      throw error;
    }

    return assetId;
  }

  resolveUploadApiUrl(path) {
    if (!path) return path;
    if (path.startsWith('http')) return path;
    if (path.startsWith('/proxy/')) {
      return `${this.uploadBase.replace(/\/api$/, '')}${path}`;
    }
    if (path.startsWith('/api/')) {
      return `${this.uploadBase}${path.replace(/^\/api/, '')}`;
    }
    return path;
  }

  uploadDirectWithProgress(uploadUrl, file, onProgress) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl);
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && event.total > 0) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          onProgress(100);
          resolve();
          return;
        }
        reject(new Error('Error al subir archivo.'));
      };
      xhr.onerror = () => reject(new Error('Error al subir archivo.'));
      file.arrayBuffer().then((body) => xhr.send(body), reject);
    });
  }

  uploadWithProgress(uploadUrl, form, onProgress) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', uploadUrl);
      xhr.withCredentials = true;
      const impersonateHeaders = this.impersonateHeaders();
      Object.entries(impersonateHeaders).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && event.total > 0) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          onProgress(100);
          resolve();
          return;
        }
        let payload = null;
        try {
          payload = JSON.parse(xhr.responseText);
        } catch {
          // ignore parse errors
        }
        reject(new Error(this.parseUploadError(payload, xhr.status)));
      };
      xhr.onerror = () => reject(new Error('Error al subir archivo.'));
      xhr.send(form);
    });
  }

  parseUploadError(payload, status) {
    if (payload?.error?.message) return payload.error.message;
    if (typeof payload?.error === 'string') return payload.error;
    if (status === 401) return 'Sesión expirada. Vuelve a iniciar sesión en la tienda.';
    if (status === 403) return 'Completa tu perfil antes de subir archivos.';
    return 'Error al subir archivo.';
  }
}

class ApapPostulacion extends HTMLElement {
  connectedCallback() {
    this.api = new ThemeApiClient({
      apiBase: this.dataset.apiBase || '/apps/apap/api/theme',
      uploadBase: this.dataset.uploadBase || '/apps/apap/api',
    });
    this.loginUrl = this.dataset.loginUrl || '/account/login';
    this.loginRedirect = this.dataset.loginRedirect || window.location.pathname;
    this.backupUrl = this.dataset.backupUrl || 'https://premio-apap.cluster.global/dashboard';
    this.backupLabel = this.dataset.backupLabel || 'Usar portal alternativo';
    this.showBackup = this.dataset.showBackup !== 'false';
    this.shopUrl = this.dataset.shopUrl || '';
    this.shopOrigin = this.dataset.shopOrigin || '';
    this.storePasswordEnabled = this.dataset.storePassword === 'true';
    this.cartAddUrl = this.dataset.cartAddUrl || '';
    this.cartClearUrl = this.dataset.cartClearUrl || '';
    this.cartUrl = this.dataset.cartUrl || '';
    this.checkoutUrl = this.dataset.checkoutUrl || '/checkout';
    this.checkoutUnits = this.parseCheckoutUnitsFromDataset();
    this.legalLinks = {
      termsUrl: this.dataset.termsUrl?.trim() || null,
      basesUrl: this.dataset.basesUrl?.trim() || null,
      privacyUrl: this.dataset.privacyUrl?.trim() || null,
    };
    this.view = 'loading';
    this.state = {
      canImpersonate: false,
      impersonation: null,
      impersonationError: null,
    };
    this._autosaveTimer = null;
    this._pendingSave = null;
    this.api.impersonateEmail = this.readImpersonateEmailFromUrl();
    this.wizard = {
      step: 1,
      submissionId: null,
      form: {},
      catalog: null,
      dirty: false,
      lastSavedPayload: null,
      suppressDirty: false,
    };
    this.init();
  }

  parseCheckoutUnitsFromDataset() {
    const pieceCount = 1;
    return {
      pieza: {
        variantNumericId: String(this.dataset.checkoutVariantPieza ?? '').trim(),
        pieceCount,
      },
      campana: {
        variantNumericId: String(this.dataset.checkoutVariantCampana ?? '').trim(),
        pieceCount,
      },
    };
  }

  applyCheckoutUnits(units) {
    if (!units?.pieza?.variantNumericId || !units?.campana?.variantNumericId) return;
    this.checkoutUnits = {
      pieza: {
        variantNumericId: String(units.pieza.variantNumericId).trim(),
        pieceCount: Number(units.pieza.pieceCount) > 0 ? Number(units.pieza.pieceCount) : 1,
      },
      campana: {
        variantNumericId: String(units.campana.variantNumericId).trim(),
        pieceCount: Number(units.campana.pieceCount) > 0 ? Number(units.campana.pieceCount) : 1,
      },
    };
  }

  getParticipantEmail() {
    return (
      this.state.participant?.email?.trim() ||
      this.dataset.customerEmail?.trim() ||
      ''
    );
  }

  getCheckoutBillingContext() {
    const p = this.state.participant ?? {};
    const customerName = String(this.dataset.customerName ?? '').trim();
    const nameParts = customerName ? customerName.split(/\s+/) : [];
    const fallbackFirstName = nameParts[0] ?? '';
    const fallbackLastName = nameParts.slice(1).join(' ');

    return {
      email: (p.invoiceEmail || p.email || this.dataset.customerInvoiceEmail || this.dataset.customerEmail || '').trim(),
      firstName: (p.firstName || fallbackFirstName).trim(),
      lastName: (p.lastName || fallbackLastName).trim(),
      company: (p.razonSocial || p.organization || this.dataset.customerRazonSocial || this.dataset.customerAgencia || '').trim(),
      phone: (p.phone || this.dataset.customerPhone || '').trim(),
      ruc: (p.ruc || this.dataset.customerRuc || '').trim(),
      invoiceContactName: (p.invoiceContactName || this.dataset.customerInvoiceContactName || '').trim(),
    };
  }

  appendCheckoutBillingParams(params, billing) {
    if (billing.email) {
      params.set('checkout[email]', billing.email);
    }

    const setBillingParam = (field, value) => {
      const trimmed = String(value ?? '').trim();
      if (trimmed) {
        params.set(`checkout[billing_address][${field}]`, trimmed);
      }
    };

    setBillingParam('first_name', billing.firstName);
    setBillingParam('last_name', billing.lastName);
    setBillingParam('company', billing.ruc);
    setBillingParam('phone', billing.phone);
    setBillingParam('country', 'PE');
  }

  buildCheckoutQueryParams() {
    const params = new URLSearchParams();
    this.appendCheckoutBillingParams(params, this.getCheckoutBillingContext());
    return params;
  }

  isThemeCheckoutConfigured() {
    return Boolean(
      this.checkoutUnits.pieza.variantNumericId && this.checkoutUnits.campana.variantNumericId,
    );
  }

  isQuotaPaymentUiEnabled() {
    const override = String(this.dataset.enableQuotaPayment ?? '').trim().toLowerCase();
    if (override === 'true' || override === '1') return true;
    if (override === 'false' || override === '0') return false;
    return ENABLE_QUOTA_PAYMENT_UI;
  }

  resolveCartUrl() {
    if (this.cartUrl) return this.cartUrl.replace(/\/$/, '');
    if (typeof Theme !== 'undefined' && Theme.routes?.cart_url) {
      return Theme.routes.cart_url.replace(/\/$/, '');
    }
    return '/cart';
  }

  resolveCartAddUrl() {
    if (this.cartAddUrl) return this.cartAddUrl;
    if (typeof Theme !== 'undefined' && Theme.routes?.cart_add_url) return Theme.routes.cart_add_url;
    return '/cart/add.js';
  }

  resolveCartClearUrl() {
    if (this.cartClearUrl) return this.cartClearUrl;
    if (typeof Theme !== 'undefined' && Theme.routes?.cart_url) {
      return `${Theme.routes.cart_url.replace(/\/$/, '')}/clear.js`;
    }
    return '/cart/clear.js';
  }

  buildCheckoutRedirectUrl() {
    const params = this.buildCheckoutQueryParams();
    const base = this.checkoutUrl || '/checkout';
    const query = params.toString();
    if (!query) return base;
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}${query}`;
  }

  parseCartErrorMessage(payload, status) {
    if (payload?.description) return payload.description;
    if (payload?.message) return payload.message;
    if (typeof payload?.error === 'string') return payload.error;
    if (status === 422) {
      return 'No se pudo agregar el producto al carrito. Verifica que los productos de inscripción estén publicados en Online Store.';
    }
    return 'No se pudo agregar las inscripciones al carrito.';
  }

  async init() {
    try {
      await this.bootstrapSession();
      this.render();
    } catch (error) {
      this.renderError(error instanceof Error ? error.message : 'No se pudo cargar.');
    }
  }

  collectStorefrontCustomerHint() {
    const body = {
      email: String(this.dataset.customerEmail ?? '').trim(),
      firstName: String(this.dataset.customerFirstName ?? '').trim(),
      lastName: String(this.dataset.customerLastName ?? '').trim(),
      phone: String(this.dataset.customerPhone ?? '').trim(),
    };
    if (!body.firstName && !body.lastName) {
      const fullName = String(this.dataset.customerName ?? '').trim();
      if (fullName) {
        const parts = fullName.split(/\s+/);
        body.firstName = parts[0] || '';
        body.lastName = parts.slice(1).join(' ');
      }
    }
    return body;
  }

  async bootstrapSession() {
    let me = await this.api.me();
    if (!me.authenticated) {
      try {
        await this.api.bootstrap(this.collectStorefrontCustomerHint());
        me = await this.api.me();
      } catch (error) {
        if (error.code === 'NOT_LOGGED_IN') {
          this.view = 'login';
          return;
        }
        throw error;
      }
    }
    this.applySessionMe(me);
    // Jurados/admin: no completar perfil de postulación; ir al portal staff.
    // Quienes pueden impersonar se quedan en el storefront.
    if (me.staffRedirect && !me.canImpersonate) {
      window.location.assign(me.staffRedirect);
      return;
    }
    if (!me.profileComplete) {
      this.view = 'profile';
      this.prefillProfile();
      try {
        const partnerData = await this.api.partnerAgencies();
        this.state.partnerAgencies = partnerData.agencies ?? [];
      } catch {
        this.state.partnerAgencies = [];
      }
      return;
    }
    this.view = 'dashboard';
    await this.loadDashboard();
  }

  applySessionMe(me) {
    this.state.participant = me.participant;
    this.state.profileComplete = me.profileComplete;
    this.state.canImpersonate = Boolean(me.canImpersonate);
    this.state.impersonation = me.impersonation ?? null;
    this.state.impersonationError = me.impersonationError ?? null;
    if (me.impersonationError) {
      this.api.impersonateEmail = '';
    } else if (this.state.impersonation?.email) {
      this.api.impersonateEmail = this.state.impersonation.email;
      this.writeImpersonateEmailToUrl(this.api.impersonateEmail);
    }
  }

  isImpersonating() {
    return Boolean(this.state.impersonation?.email);
  }

  readImpersonateEmailFromUrl() {
    try {
      return new URLSearchParams(window.location.search).get('as')?.trim().toLowerCase() || '';
    } catch {
      return '';
    }
  }

  writeImpersonateEmailToUrl(email) {
    const url = new URL(window.location.href);
    const normalized = String(email ?? '').trim().toLowerCase();
    if (normalized) url.searchParams.set('as', normalized);
    else url.searchParams.delete('as');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }

  renderImpersonationBanner() {
    if (!this.isImpersonating()) return '';
    const target = this.state.impersonation;
    const name = [target.firstName, target.lastName].filter(Boolean).join(' ').trim();
    const org = String(target.organization ?? '').trim();
    const extra = [name && name.toLowerCase() !== target.email ? name : '', org].filter(Boolean).join(' · ');
    return `<div class="apap-postulacion__alert apap-postulacion__alert--warning">Vista de solo lectura de ${this.escape(target.email)}${extra ? ` (${this.escape(extra)})` : ''}.</div>`;
  }

  disconnectedCallback() {
    document.getElementById('apap-impersonate-dock')?.remove();
  }

  syncImpersonationDock() {
    const existing = document.getElementById('apap-impersonate-dock');
    if (!this.state.canImpersonate) {
      existing?.remove();
      return;
    }

    let dock = existing;
    if (!dock) {
      dock = document.createElement('div');
      dock.id = 'apap-impersonate-dock';
      dock.className = 'apap-impersonate-dock';
      document.body.appendChild(dock);
      dock.addEventListener('submit', (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement) || form.getAttribute('data-form') !== 'impersonate') return;
        event.preventDefault();
        const email = String(new FormData(form).get('email') ?? '').trim();
        void this.applyImpersonation(email);
      });
      dock.addEventListener('click', (event) => {
        const button = event.target instanceof Element ? event.target.closest('[data-action="impersonate-clear"]') : null;
        if (!button) return;
        event.preventDefault();
        void this.applyImpersonation('');
      });
    }

    const activeEmail = this.state.impersonation?.email || this.readImpersonateEmailFromUrl() || this.api.impersonateEmail || '';
    const error = this.state.impersonationError?.message;
    const active = this.isImpersonating();
    dock.innerHTML = `
      <p class="apap-impersonate-dock__title">Ver como</p>
      ${
        active
          ? `<p class="apap-impersonate-dock__status">Solo lectura<br><strong>${this.escape(this.state.impersonation.email)}</strong></p>`
          : '<p class="apap-impersonate-dock__status">Tu sesión</p>'
      }
      <form class="apap-impersonate-dock__form" data-form="impersonate">
        <input class="apap-impersonate-dock__input" type="email" name="email" value="${this.escapeAttr(activeEmail)}" placeholder="correo@agencia.pe" autocomplete="off" required>
        <button type="submit" class="apap-impersonate-dock__btn">Ver</button>
      </form>
      ${error ? `<p class="apap-impersonate-dock__error">${this.escape(error)}</p>` : ''}
      ${active ? '<button type="button" class="apap-impersonate-dock__clear" data-action="impersonate-clear">Salir</button>' : ''}
    `;
  }

  async applyImpersonation(email) {
    const normalized = String(email ?? '').trim().toLowerCase();
    if (normalized) {
      try {
        await this.api.lookupImpersonate(normalized);
      } catch (error) {
        this.state.impersonationError = {
          code: error.code || 'IMPERSONATION_NOT_FOUND',
          message: error instanceof Error ? error.message : 'No hay un participante con ese correo.',
        };
        this.syncImpersonationDock();
        return;
      }
    }
    this.api.impersonateEmail = normalized;
    this.writeImpersonateEmailToUrl(normalized);
    this.view = 'loading';
    this.innerHTML = '<div class="apap-postulacion__loading"><p class="apap-postulacion__loading-text">Cargando postulaciones…</p></div>';
    this.syncImpersonationDock();
    try {
      await this.bootstrapSession();
      this.render();
    } catch (error) {
      this.renderError(error instanceof Error ? error.message : 'No se pudo cargar.');
    }
  }

  prefillProfile() {
    const p = this.state.participant ?? {};
    const esSocioDataset = String(this.dataset.customerEsSocio ?? '').trim().toLowerCase();
    const isApapPartner =
      typeof p.isApapPartner === 'boolean'
        ? p.isApapPartner
        : esSocioDataset === 'true' || esSocioDataset === '1';
    this.wizard.form = {
      firstName: p.firstName || '',
      lastName: p.lastName || '',
      organization: p.organization || this.dataset.customerAgencia || '',
      phone: p.phone || '',
      ruc: p.ruc || this.dataset.customerRuc || '',
      razonSocial: p.razonSocial || this.dataset.customerRazonSocial || '',
      isApapPartner,
      partnerAgencyId: p.partnerAgencyId || this.dataset.customerSocioId || '',
      invoiceEmail: p.invoiceEmail || '',
      invoiceContactName: p.invoiceContactName || '',
    };
  }

  collectProfileFormBody(formData) {
    const isApapPartner = formData.get('isApapPartner') === 'true';
    return {
      firstName: String(formData.get('firstName') ?? '').trim(),
      lastName: String(formData.get('lastName') ?? '').trim(),
      phone: String(formData.get('phone') ?? '').trim(),
      isApapPartner,
      partnerAgencyId: isApapPartner ? String(formData.get('partnerAgencyId') ?? '').trim() : '',
      organization: isApapPartner ? '' : String(formData.get('organization') ?? '').trim(),
      ruc: isApapPartner ? '' : String(formData.get('ruc') ?? '').trim(),
      razonSocial: isApapPartner ? '' : String(formData.get('razonSocial') ?? '').trim(),
      invoiceEmail: String(formData.get('invoiceEmail') ?? '').trim(),
      invoiceContactName: String(formData.get('invoiceContactName') ?? '').trim(),
    };
  }

  renderToolbarAccountButton() {
    return `<button type="button" class="apap-postulacion__btn apap-postulacion__btn--ghost apap-postulacion__btn--sm" data-action="open-account">Mi cuenta</button>`;
  }

  async ensurePartnerAgenciesLoaded() {
    if (this.state.partnerAgencies?.length) return;
    try {
      const data = await this.api.partnerAgencies();
      this.state.partnerAgencies = data.agencies ?? [];
    } catch {
      this.state.partnerAgencies = [];
    }
  }

  resolvePartnerAgencyName(participant) {
    const id = String(participant?.partnerAgencyId ?? '').trim();
    if (!id) return participant?.organization || '';
    const agency = (this.state.partnerAgencies ?? []).find((item) => item.id === id);
    return agency?.name || participant?.organization || '';
  }

  async openAccount(button) {
    await this.withButtonLoading(button, () => this.loadAccountView(), 'Cargando…');
  }

  renderAccount() {
    const f = this.wizard.form;
    const agencies = this.state.partnerAgencies ?? [];
    const isPartner = Boolean(f.isApapPartner);
    const email = this.state.participant?.email || '';
    const readOnly = this.isImpersonating();
    const disabled = readOnly ? 'disabled' : '';
    const flash = this.state.accountFlash
      ? `<div class="apap-postulacion__alert apap-postulacion__alert--${this.state.accountFlashTone === 'warning' ? 'warning' : 'success'}">${this.escape(this.state.accountFlash)}</div>`
      : '';

    return `
      <div class="apap-postulacion__panel">
        ${this.renderImpersonationBanner()}
        ${flash}
        <div class="apap-postulacion__toolbar">
          <div>
            <h2 class="apap-postulacion__toolbar-title">${readOnly ? 'Cuenta del participante' : 'Mi cuenta'}</h2>
            <p class="apap-postulacion__item-meta">${readOnly ? 'Datos de agencia y facturación en solo lectura.' : 'Actualiza los datos de tu agencia y la información para facturación.'}</p>
          </div>
          <button type="button" class="apap-postulacion__btn apap-postulacion__btn--primary" data-action="back-dashboard">Volver</button>
        </div>
        <form class="apap-postulacion__form" data-form="account">
          <div class="apap-postulacion__card-stack">
            ${this.renderCard(
              'Datos personales y agencia',
              `<p class="apap-postulacion__item-meta">Correo de acceso: ${this.escape(email)}</p>
              <div class="apap-postulacion__field-grid apap-postulacion__field-grid--2">
                ${this.renderTextField('firstName', 'Nombre', f.firstName, null, { required: true, disabled: readOnly })}
                ${this.renderTextField('lastName', 'Apellido', f.lastName, null, { required: true, disabled: readOnly })}
                ${this.renderTextField('phone', 'Teléfono', f.phone, null, { required: true, disabled: readOnly })}
              </div>
              <fieldset class="apap-postulacion__field" ${disabled}>
                <legend class="apap-postulacion__field-label">¿Tu agencia es socia APAP?</legend>
                <div class="apap-postulacion__radio-row">
                  <label class="apap-postulacion__radio">
                    <input type="radio" name="isApapPartner" value="true" data-profile-partner-toggle ${isPartner ? 'checked' : ''} ${disabled}>
                    <span>Sí</span>
                  </label>
                  <label class="apap-postulacion__radio">
                    <input type="radio" name="isApapPartner" value="false" data-profile-partner-toggle ${!isPartner ? 'checked' : ''} ${disabled}>
                    <span>No</span>
                  </label>
                </div>
              </fieldset>
              <div data-profile-partner-fields ${isPartner ? '' : 'hidden'}>
                ${this.renderPartnerAgencySelect(f.partnerAgencyId, agencies, f.organization, { disabled: readOnly })}
              </div>
              <div data-profile-non-partner-fields ${isPartner ? 'hidden' : ''}>
                <div class="apap-postulacion__field-grid apap-postulacion__field-grid--2">
                  ${this.renderTextField('organization', 'Nombre de la agencia', f.organization, null, { required: !isPartner, disabled: readOnly })}
                  ${this.renderTextField('ruc', 'RUC', f.ruc, null, { required: !isPartner, pattern: '\\d{11}', disabled: readOnly })}
                  ${this.renderTextField('razonSocial', 'Razón social', f.razonSocial, null, { required: !isPartner, disabled: readOnly })}
                </div>
              </div>`,
            )}
            ${this.renderCard(
              'Facturación',
              `<p class="apap-postulacion__item-meta">Indica a quién debe enviarse la factura de tus inscripciones.</p>
              <div class="apap-postulacion__field-grid apap-postulacion__field-grid--2">
                ${this.renderTextField('invoiceEmail', 'Correo de facturación', f.invoiceEmail, 'contabilidad@agencia.com', { type: 'email', disabled: readOnly })}
                ${this.renderTextField('invoiceContactName', 'Contacto de facturación', f.invoiceContactName, 'Área de contabilidad o persona responsable', { disabled: readOnly })}
              </div>`,
              { hint: 'La factura se enviará al correo de facturación. El contacto es opcional.' },
            )}
          </div>
          <div data-account-error></div>
          ${
            readOnly
              ? ''
              : `<div class="apap-postulacion__wizard-footer">
            <div class="apap-postulacion__wizard-footer-actions">
              <span></span>
              <button type="submit" class="apap-postulacion__btn apap-postulacion__btn--primary">Guardar cambios</button>
            </div>
          </div>`
          }
        </form>
      </div>
    `;
  }

  bindAccountActions() {
    this.bindProfilePartnerToggle();
    this.querySelector('[data-action="back-dashboard"]')?.addEventListener('click', async (event) => {
      const button = event.currentTarget;
      if (!(button instanceof HTMLButtonElement)) return;
      await this.withButtonLoading(button, async () => {
        this.state.accountFlash = null;
        this.view = 'dashboard';
        await this.loadDashboard();
        this.render();
      }, 'Cargando…');
    });
    this.querySelector('[data-form="account"]')?.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (this.isImpersonating()) return;
      const form = /** @type {HTMLFormElement} */ (event.currentTarget);
      const body = this.collectProfileFormBody(new FormData(form));
      const errorEl = this.querySelector('[data-account-error]');
      try {
        const data = await this.api.saveProfile(body);
        this.state.participant = data.participant;
        this.prefillProfile();
        // Refresh cart/checkout units so partner vs regular prices update immediately.
        try {
          await this.loadDashboard();
        } catch {
          // Keep account save success even if dashboard refresh fails.
        }
        this.state.accountFlash = data.warning ?? 'Tus datos se guardaron correctamente.';
        this.state.accountFlashTone = data.warning ? 'warning' : 'success';
        this.render();
      } catch (error) {
        if (errorEl) {
          errorEl.innerHTML = `<div class="apap-postulacion__alert apap-postulacion__alert--error">${this.escape(error.message)}</div>`;
        }
      }
    });
  }

  async loadDashboard() {
    return perfStep('loadDashboard', async () => {
      this.state.dashboard = await this.api.dashboard();
      this.applyCheckoutUnits(this.state.dashboard?.checkoutUnits);
      const params = new URLSearchParams(window.location.search);
      if (params.get('compra') === 'ok') {
        this.state.flash = 'Pago recibido. Las inscripciones se registrarán en unos minutos.';
      }
      await this.applyStorefrontDeepLink(params);
    });
  }

  replaceBrowserQuery(params) {
    const nextQuery = params.toString();
    const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash}`;
    window.history.replaceState({}, '', nextUrl);
  }

  async loadAccountView() {
    const me = await this.api.me();
    this.state.participant = me.participant;
    await this.ensurePartnerAgenciesLoaded();
    this.prefillProfile();
    this.view = 'account';
    this.render();
  }

  async applyStorefrontDeepLink(params = new URLSearchParams(window.location.search)) {
    const view = params.get('view')?.trim();
    try {
      if (view === 'account') {
        params.delete('view');
        await this.loadAccountView();
        return;
      }
      if (view === 'comprar') {
        params.delete('view');
        this.view = 'comprar';
        this.state.comprar = await this.api.comprarCatalog();
        this.render();
        return;
      }
      if (view === 'view') {
        const submissionId = params.get('submission')?.trim();
        params.delete('view');
        if (submissionId) {
          params.delete('submission');
          await this.openView(submissionId);
        }
        return;
      }
      const submissionId = params.get('submission')?.trim();
      if (!submissionId) return;
      const stepRaw = Number(params.get('step'));
      const step = Number.isFinite(stepRaw) && stepRaw >= 1 ? stepRaw : 1;
      params.delete('submission');
      params.delete('step');
      await this.openWizard(submissionId, { step });
    } catch (error) {
      this.state.flash =
        error instanceof Error
          ? error.message
          : 'No se pudo abrir la vista solicitada.';
    } finally {
      this.replaceBrowserQuery(params);
    }
  }

  renderError(message) {
    this.innerHTML = `
      <div class="apap-postulacion__panel">
        <div class="apap-postulacion__alert apap-postulacion__alert--error">${this.escape(message)}</div>
        <button type="button" class="apap-postulacion__btn" data-action="retry">Reintentar</button>
      </div>
    `;
    this.querySelector('[data-action="retry"]')?.addEventListener('click', () => {
      this.view = 'loading';
      this.innerHTML = '<div class="apap-postulacion__loading"><p class="apap-postulacion__loading-text">Cargando postulaciones…</p></div>';
      this.init();
    });
    this.syncImpersonationDock();
  }

  render() {
    if (this.view !== 'wizard') {
      this.stopWizardAutosave();
    }

    switch (this.view) {
      case 'login':
        this.innerHTML = this.renderLogin();
        break;
      case 'profile':
        this.innerHTML = this.renderProfile();
        this.bindProfileForm();
        break;
      case 'dashboard':
        this.innerHTML = this.renderDashboard();
        this.bindDashboardActions();
        break;
      case 'account':
        this.innerHTML = this.renderAccount();
        this.bindAccountActions();
        break;
      case 'wizard':
        this.innerHTML = this.renderWizard();
        this.bindWizardForm();
        this.startWizardAutosave();
        break;
      case 'view':
        this.innerHTML = this.renderViewSubmission();
        this.bindViewActions();
        break;
      case 'comprar':
        this.innerHTML = this.renderComprar();
        this.bindComprarActions();
        break;
      default:
        this.innerHTML = '<div class="apap-postulacion__loading"><p class="apap-postulacion__loading-text">Cargando postulaciones…</p></div>';
    }
    this.querySelector('.apap-postulacion__footer')?.remove();
    if (this.view === 'dashboard' || this.view === 'wizard') {
      this.startCountdownTimer();
    } else {
      this.stopCountdownTimer();
    }
    this.syncImpersonationDock();
  }

  getCountdownRemaining(closesAt) {
    const total = new Date(closesAt).getTime() - Date.now();
    if (total <= 0) {
      return { total: 0, days: 0, hours: 0, minutes: 0, seconds: 0 };
    }
    return {
      total,
      days: Math.floor(total / (1000 * 60 * 60 * 24)),
      hours: Math.floor(total / (1000 * 60 * 60)) % 24,
      minutes: Math.floor(total / (1000 * 60)) % 60,
      seconds: Math.floor(total / 1000) % 60,
    };
  }

  padCountdown(value) {
    return String(value).padStart(2, '0');
  }

  renderCountdownUnits(remaining) {
    const units = [
      { label: 'Días', value: String(remaining.days) },
      { label: 'Horas', value: this.padCountdown(remaining.hours) },
      { label: 'Min', value: this.padCountdown(remaining.minutes) },
      { label: 'Seg', value: this.padCountdown(remaining.seconds) },
    ];
    return units
      .map(
        (unit, index) => `
          <div class="apap-postulacion__countdown-unit">
            ${index > 0 ? '<span class="apap-postulacion__countdown-separator" aria-hidden="true">:</span>' : ''}
            <span class="apap-postulacion__countdown-value">${this.escape(unit.value)}</span>
            <span class="apap-postulacion__countdown-label">${this.escape(unit.label)}</span>
          </div>
        `,
      )
      .join('');
  }

  getCountdownDisplayEndsAt(closesAt) {
    const date = new Date(closesAt);
    if (Number.isNaN(date.getTime())) return closesAt;
    return new Date(date.getTime() + COUNTDOWN_DISPLAY_OFFSET_MS).toISOString();
  }

  renderEditionCountdown() {
    const phase = this.state.dashboard?.contestPhase;
    if (!phase?.countdownEndsAt) return '';
    const displayEndsAt = this.getCountdownDisplayEndsAt(phase.countdownEndsAt);
    return `
      <div
        class="apap-postulacion__countdown"
        data-countdown
        data-closes-at="${this.escapeAttr(displayEndsAt)}"
        data-closed-label="${this.escapeAttr(phase.label)}"
        aria-live="polite"
      >
        <p class="apap-postulacion__countdown-eyebrow">Cierra en</p>
        <div class="apap-postulacion__countdown-grid" data-countdown-live></div>
      </div>
    `;
  }

  updateCountdownElements() {
    let hasActiveCountdown = false;
    this.querySelectorAll('[data-countdown]').forEach((root) => {
      const closesAt = root.getAttribute('data-closes-at');
      if (!closesAt) return;
      const closedLabel = root.getAttribute('data-closed-label') || 'Inscripciones cerradas';
      const remaining = this.getCountdownRemaining(closesAt);
      if (remaining.total <= 0) {
        root.classList.add('apap-postulacion__countdown--closed');
        root.innerHTML = `<p class="apap-postulacion__countdown-closed">${this.escape(closedLabel)}</p>`;
        return;
      }
      hasActiveCountdown = true;
      root.classList.remove('apap-postulacion__countdown--closed');
      const live = root.querySelector('[data-countdown-live]');
      if (live) {
        live.innerHTML = this.renderCountdownUnits(remaining);
      } else if (!root.querySelector('.apap-postulacion__countdown-eyebrow')) {
        root.innerHTML = `
          <p class="apap-postulacion__countdown-eyebrow">Cierra en</p>
          <div class="apap-postulacion__countdown-grid" data-countdown-live>${this.renderCountdownUnits(remaining)}</div>
        `;
      }
    });
    if (!hasActiveCountdown) {
      this.stopCountdownTimer();
    }
  }

  stopCountdownTimer() {
    if (this._countdownTimer) {
      window.clearInterval(this._countdownTimer);
      this._countdownTimer = null;
    }
  }

  startCountdownTimer() {
    this.stopCountdownTimer();
    if (!this.querySelector('[data-countdown]')) return;
    this.updateCountdownElements();
    this._countdownTimer = window.setInterval(() => this.updateCountdownElements(), 1000);
  }

  renderBackupLink() {
    if (!this.showBackup) return;
    const footer = document.createElement('div');
    footer.className = 'apap-postulacion__footer';
    footer.innerHTML = `<a href="${this.escapeAttr(this.backupUrl)}" target="_blank" rel="noopener noreferrer">${this.escape(this.backupLabel)}</a>`;
    this.appendChild(footer);
  }

  renderLogin() {
    const returnUrl = encodeURIComponent(this.loginRedirect || window.location.href);
    const href = `${this.loginUrl}${this.loginUrl.includes('?') ? '&' : '?'}return_url=${returnUrl}`;
    return `
      <div class="apap-postulacion__panel">
        ${this.renderCard(
          'Inicia sesión o crea una cuenta de usuario',
          '<a class="apap-postulacion__btn apap-postulacion__btn--primary" href="' +
            this.escapeAttr(href) +
            '">Iniciar sesión</a>',
          { hint: 'Necesitas una cuenta activa para poder inscribirte.' },
        )}
      </div>
    `;
  }

  renderProfile() {
    const f = this.wizard.form;
    const agencies = this.state.partnerAgencies ?? [];
    const isPartner = Boolean(f.isApapPartner);
    const readOnly = this.isImpersonating();
    const disabled = readOnly ? 'disabled' : '';
    return `
      <div class="apap-postulacion__panel">
        ${this.renderImpersonationBanner()}
        <div class="apap-postulacion__toolbar">
          <div>
            <h2 class="apap-postulacion__toolbar-title">${readOnly ? 'Perfil del participante' : 'Completa tu perfil'}</h2>
            <p class="apap-postulacion__item-meta">${readOnly ? 'Datos de perfil en solo lectura.' : 'Confirma tus datos de agencia para continuar.'}</p>
          </div>
        </div>
        <form class="apap-postulacion__form" data-form="profile">
          <div class="apap-postulacion__card-stack">
            ${this.renderCard(
              'Datos personales',
              `<div class="apap-postulacion__field-grid apap-postulacion__field-grid--2">
                ${this.renderTextField('firstName', 'Nombre', f.firstName, null, { required: true, disabled: readOnly })}
                ${this.renderTextField('lastName', 'Apellido', f.lastName, null, { required: true, disabled: readOnly })}
                ${this.renderTextField('phone', 'Teléfono', f.phone, null, { required: true, disabled: readOnly })}
              </div>`,
            )}
            ${this.renderCard(
              'Datos de agencia',
              `<fieldset class="apap-postulacion__field" ${disabled}>
                <legend class="apap-postulacion__field-label">¿Tu agencia es socia APAP?</legend>
                <div class="apap-postulacion__radio-row">
                  <label class="apap-postulacion__radio">
                    <input type="radio" name="isApapPartner" value="true" data-profile-partner-toggle ${isPartner ? 'checked' : ''} ${disabled}>
                    <span>Sí</span>
                  </label>
                  <label class="apap-postulacion__radio">
                    <input type="radio" name="isApapPartner" value="false" data-profile-partner-toggle ${!isPartner ? 'checked' : ''} ${disabled}>
                    <span>No</span>
                  </label>
                </div>
              </fieldset>
              <div data-profile-partner-fields ${isPartner ? '' : 'hidden'}>
                ${this.renderPartnerAgencySelect(f.partnerAgencyId, agencies, f.organization, { disabled: readOnly })}
              </div>
              <div data-profile-non-partner-fields ${isPartner ? 'hidden' : ''}>
                <div class="apap-postulacion__field-grid apap-postulacion__field-grid--2">
                  ${this.renderTextField('organization', 'Nombre de la agencia', f.organization, null, { required: !isPartner, disabled: readOnly })}
                  ${this.renderTextField('ruc', 'RUC', f.ruc, null, { required: !isPartner, pattern: '\\d{11}', disabled: readOnly })}
                  ${this.renderTextField('razonSocial', 'Razón social', f.razonSocial, null, { required: !isPartner, disabled: readOnly })}
                </div>
              </div>`,
            )}
            ${this.renderCard(
              'Facturación',
              `<p class="apap-postulacion__item-meta">Indica a quién debe enviarse la factura de tus inscripciones.</p>
              <div class="apap-postulacion__field-grid apap-postulacion__field-grid--2">
                ${this.renderTextField('invoiceEmail', 'Correo de facturación', f.invoiceEmail, 'contabilidad@agencia.com', { type: 'email', disabled: readOnly })}
                ${this.renderTextField('invoiceContactName', 'Contacto de facturación', f.invoiceContactName, 'Área de contabilidad o persona responsable', { disabled: readOnly })}
              </div>`,
              { hint: 'La factura se enviará al correo de facturación. El contacto es opcional.' },
            )}
          </div>
          <div data-profile-error></div>
          ${
            readOnly
              ? ''
              : `<div class="apap-postulacion__wizard-footer">
            <div class="apap-postulacion__wizard-footer-actions">
              <span></span>
              <button type="submit" class="apap-postulacion__btn apap-postulacion__btn--primary">Guardar y continuar</button>
            </div>
          </div>`
          }
        </form>
      </div>
    `;
  }

  renderPartnerAgencySelect(selectedId, agencies, selectedLabel = '', { disabled = false } = {}) {
    const list = Array.isArray(agencies) ? [...agencies] : [];
    // Conserva la agencia actual aunque haya sido desactivada en el catálogo.
    if (selectedId && !list.some((agency) => agency.id === selectedId)) {
      list.unshift({
        id: selectedId,
        name: selectedLabel || selectedId,
      });
    }
    const options = [
      '<option value="">Selecciona tu agencia socia</option>',
      ...list.map(
        (agency) =>
          `<option value="${this.escapeAttr(agency.id)}" ${agency.id === selectedId ? 'selected' : ''}>${this.escape(agency.name)}</option>`,
      ),
    ];
    return `<div class="apap-postulacion__field">${this.renderFieldLabel('Agencia socia APAP', { required: true })}<select class="apap-postulacion__input" name="partnerAgencyId" data-profile-partner-select ${disabled ? 'disabled' : 'required'}>${options.join('')}</select></div>`;
  }

  bindProfilePartnerToggle() {
    // Account and onboarding profile forms share the same partner/non-partner fields.
    const form = this.querySelector('[data-form="account"], [data-form="profile"]');
    if (!form) return;

    const partnerFields = form.querySelector('[data-profile-partner-fields]');
    const nonPartnerFields = form.querySelector('[data-profile-non-partner-fields]');
    const partnerSelect = form.querySelector('[data-profile-partner-select]');
    const nonPartnerInputs = form.querySelectorAll('[data-profile-non-partner-fields] [name]');

    const syncVisibility = () => {
      const isPartner = form.querySelector('input[name="isApapPartner"][value="true"]')?.checked;
      if (partnerFields) partnerFields.hidden = !isPartner;
      if (nonPartnerFields) nonPartnerFields.hidden = Boolean(isPartner);
      if (partnerSelect) partnerSelect.required = Boolean(isPartner);
      nonPartnerInputs.forEach((input) => {
        input.required = !isPartner;
        if (isPartner) input.removeAttribute('required');
      });
    };

    form.querySelectorAll('[data-profile-partner-toggle]').forEach((input) => {
      input.addEventListener('change', syncVisibility);
    });
    syncVisibility();
  }

  bindProfileForm() {
    this.bindProfilePartnerToggle();
    this.querySelector('[data-form="profile"]')?.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (this.isImpersonating()) return;
      const form = /** @type {HTMLFormElement} */ (event.currentTarget);
      const body = this.collectProfileFormBody(new FormData(form));
      const errorEl = this.querySelector('[data-profile-error]');
      try {
        const data = await this.api.saveProfile(body);
        this.state.participant = data.participant;
        this.state.profileComplete = true;
        this.state.flash = data.warning ?? null;
        this.state.flashTone = data.warning ? 'warning' : null;
        this.view = 'dashboard';
        await this.loadDashboard();
        this.render();
      } catch (error) {
        if (errorEl) {
          errorEl.innerHTML = `<div class="apap-postulacion__alert apap-postulacion__alert--error">${this.escape(error.message)}</div>`;
        }
      }
    });
  }

  renderDashboard() {
    const d = this.state.dashboard;
    if (!d?.edition) {
      return `<div class="apap-postulacion__panel">${this.renderImpersonationBanner()}<p>No hay una edición activa en este momento.</p></div>`;
    }
    const submissions = d.submissions ?? [];
    const phaseLabel = d.contestPhase?.label ?? '';
    const registrationOpen = this.isRegistrationOpen(d.contestPhase);
    const canCreateDraft = registrationOpen && d.draftQuota.canCreateDraft;
    const createHeaderAction = canCreateDraft ? this.renderCreateSubmissionButton() : '';
    const createEmptyAction = this.isImpersonating()
      ? ''
      : this.renderCreateSubmissionAction({
          registrationOpen,
          canCreateDraft,
          draftCount: d.draftQuota.draftCount,
          maxDrafts: d.draftQuota.maxDrafts,
        });
    const flash = this.state.flash
      ? `<div class="apap-postulacion__alert apap-postulacion__alert--${this.state.flashTone === 'warning' ? 'warning' : 'success'}">${this.escape(this.state.flash)}</div>`
      : '';
    return `
      <div class="apap-postulacion__page-shell">
        <div class="apap-postulacion__page-actions">
          ${this.renderToolbarAccountButton()}
        </div>
        <div class="apap-postulacion__panel">
          ${this.renderImpersonationBanner()}
          ${flash}
          <div class="apap-postulacion__toolbar">
            <div class="apap-postulacion__toolbar-head">
              <div>
                <h2 class="apap-postulacion__toolbar-title">${this.escape(d.edition.name)}</h2>
                ${
                  phaseLabel
                    ? `<p class="apap-postulacion__item-meta">${this.escape(phaseLabel)}</p>`
                    : `<p class="apap-postulacion__item-meta">Cierra el ${new Date(d.edition.closesAt).toLocaleDateString('es-PE')}</p>`
                }
              </div>
              ${this.renderEditionCountdown()}
            </div>
          </div>
        <div class="apap-postulacion__dashboard-summary">
          ${this.renderDashboardQuotaPanel(registrationOpen, d.quota)}
          ${registrationOpen || this.isImpersonating() ? this.renderDashboardCartPanel() : ''}
        </div>
        <section class="apap-postulacion__submissions" aria-labelledby="dashboard-submissions-heading">
          <div class="apap-postulacion__list-header">
            <div class="apap-postulacion__list-header-main">
              <h3 id="dashboard-submissions-heading">${this.isImpersonating() ? 'Postulaciones' : 'Tus postulaciones'}</h3>
              <span class="apap-postulacion__item-meta">${submissions.length} en total</span>
            </div>
            ${submissions.length ? createHeaderAction : ''}
          </div>
          ${
            submissions.length
              ? `<ul class="apap-postulacion__list">${this.sortDashboardSubmissions(submissions)
                  .map((s) => this.renderSubmissionItem(s))
                  .join('')}</ul>`
              : `<div class="apap-postulacion__empty">
                  <p>${this.isImpersonating() ? 'Este usuario aún no tiene postulaciones.' : `Aún no tienes postulaciones.${registrationOpen ? ' Crea una nueva para comenzar.' : ''}`}</p>
                  ${createEmptyAction}
                </div>`
          }
        </section>
        <div data-dashboard-error></div>
        </div>
      </div>
    `;
  }

  isRegistrationOpen(contestPhase) {
    const phase = contestPhase?.phase;
    return phase === 'INSCRIPCIONES_ABIERTAS' || phase === 'INSCRIPCIONES_EXTEMPORANEAS';
  }

  renderDashboardQuotaPanel(registrationOpen, quota) {
    if (!registrationOpen) {
      return `
        <div class="apap-postulacion__stat apap-postulacion__stat--credits apap-postulacion__stat--closed">
          <div class="apap-postulacion__stat-label">Inscripciones cerradas</div>
          <p class="apap-postulacion__closed-message">
            Las inscripciones ya se encuentran cerradas. Tus piezas estarán en proceso de verificación en los próximos días;
            si existe alguna observación, recibirás un correo.
          </p>
        </div>
      `;
    }

    return `
      <div class="apap-postulacion__stat apap-postulacion__stat--credits">
        <div class="apap-postulacion__stat-label">Inscripciones disponibles</div>
        <div class="apap-postulacion__credits-rows">
          <div class="apap-postulacion__credits-row">
            <span>Piezas</span>
            <strong>${quota?.pieza?.available ?? 0}</strong>
          </div>
          <div class="apap-postulacion__credits-row">
            <span>Campañas</span>
            <strong>${quota?.campana?.available ?? 0}</strong>
          </div>
        </div>
      </div>
    `;
  }

  sortDashboardSubmissions(submissions) {
    const rank = (status) => {
      if (status === 'DRAFT') return 0;
      if (status === 'OBSERVED') return 1;
      return 2;
    };
    return [...(submissions ?? [])].sort((a, b) => {
      const byStatus = rank(a.status) - rank(b.status);
      if (byStatus !== 0) return byStatus;
      const aTime = new Date(a.updatedAt || 0).getTime();
      const bTime = new Date(b.updatedAt || 0).getTime();
      return bTime - aTime;
    });
  }

  renderAddIcon() {
    return `<svg class="apap-postulacion__btn-icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M8 3v10M3 8h10" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"></path>
    </svg>`;
  }

  renderLinkIcon() {
    return `<svg class="apap-postulacion__btn-icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M6.67 9.33a3.33 3.33 0 0 0 4.71 0l1.67-1.67a3.33 3.33 0 0 0-4.71-4.71L7.17 4.08" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"></path>
      <path d="M9.33 6.67a3.33 3.33 0 0 0-4.71 0L2.95 8.34a3.33 3.33 0 0 0 4.71 4.71L8.83 11.92" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"></path>
    </svg>`;
  }

  renderShareCheckoutButton({
    context,
    quotaType = '',
    quantity = '',
    submissionId = '',
    label = 'Copiar enlace de pago',
    size = 'sm',
  } = {}) {
    const sm = size === 'sm' ? ' apap-postulacion__btn--sm' : '';
    const quotaAttrs =
      quotaType && quantity
        ? ` data-quota-type="${this.escapeAttr(quotaType)}" data-quota-quantity="${this.escapeAttr(String(quantity))}"`
        : '';
    const submissionAttr = submissionId
      ? ` data-submission-id="${this.escapeAttr(submissionId)}"`
      : '';
    return `<button type="button" class="apap-postulacion__btn apap-postulacion__btn--secondary${sm}" data-action="share-checkout-link" data-checkout-context="${this.escapeAttr(context)}"${quotaAttrs}${submissionAttr}>${this.renderLinkIcon()}<span data-share-label>${this.escape(label)}</span></button>`;
  }

  renderEditIcon() {
    return `<svg class="apap-postulacion__btn-icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M11.4 2.6a1.5 1.5 0 0 1 2.1 2.1L5.4 12.8 2.5 13.5l.7-2.9 8.2-8Z" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"></path>
    </svg>`;
  }

  renderEditButton(id, { variant = 'primary' } = {}) {
    return `<button type="button" class="apap-postulacion__btn apap-postulacion__btn--${variant} apap-postulacion__btn--sm" data-action="edit" data-id="${this.escapeAttr(id)}">${this.renderEditIcon()}<span>Editar</span></button>`;
  }

  renderCreateSubmissionButton() {
    return `<button type="button" class="apap-postulacion__btn apap-postulacion__btn--primary apap-postulacion__btn--sm" data-action="create">${this.renderAddIcon()}<span>Nueva inscripción</span></button>`;
  }

  renderCreateSubmissionAction({ registrationOpen, canCreateDraft, draftCount, maxDrafts }) {
    if (!registrationOpen) {
      return '<div class="apap-postulacion__alert apap-postulacion__alert--warning">Las inscripciones están cerradas.</div>';
    }

    if (canCreateDraft) {
      return this.renderCreateSubmissionButton();
    }

    return `<p class="apap-postulacion__item-meta">Tienes ${draftCount} borrador(es) abierto(s) (máximo ${maxDrafts}). Elimina uno para crear otro.</p>`;
  }

  renderDashboardCartPanel() {
    const cart = this.state.dashboard?.cart;
    if (!cart?.drafts?.length) return '';

    const { toPurchasePieza, toPurchaseCampana } = cart;
    const hasShortfall = toPurchasePieza > 0 || toPurchaseCampana > 0;
    if (!hasShortfall) return '';

    // Backend already returns only drafts not covered by available credits.
    const payableDrafts = cart.drafts.filter((draft) => Number(draft.slots || draft.categoryCount) > 0);
    if (!payableDrafts.length) return '';

    const canCheckout = this.isThemeCheckoutConfigured() && cart.canCheckout;
    const splitCheckout = this.shouldSplitDashboardCart(cart);
    const draftLines = payableDrafts
      .map((draft) => this.renderDashboardCartLine(draft, { splitCheckout, canCheckout }))
      .join('');

    const checkoutShareHint =
      'En caso de que no vayas a hacer tú el pago, puedes compartir el enlace de pago con el equipo de contabilidad de la agencia para que realicen el pago; indícales que no cambien el correo que proporcionaste en el registro. La factura llegará al email de facturación que proporcionaste al momento del registro.';

    const actionsHtml = canCheckout
      ? `<div class="apap-postulacion__quota-actions">
          <button type="button" class="apap-postulacion__btn apap-postulacion__btn--primary" data-action="comprar-dashboard-cart">Pagar todo en conjunto</button>
          ${this.renderShareCheckoutButton({ context: 'dashboard-cart', label: 'Copiar enlace de pago', size: '' })}
        </div>
        <p class="apap-postulacion__quota-hint">${checkoutShareHint}</p>`
      : this.isThemeCheckoutConfigured()
        ? '<div class="apap-postulacion__alert apap-postulacion__alert--warning">No se pudo preparar el checkout. Revisa la configuración de productos.</div>'
        : '<div class="apap-postulacion__alert apap-postulacion__alert--warning">El checkout no está disponible en este entorno.</div>';

    return `
      <section class="apap-postulacion__quota-card apap-postulacion__dashboard-cart" aria-labelledby="dashboard-cart-heading">
        <div class="apap-postulacion__quota-card-header">
          <h3 id="dashboard-cart-heading" class="apap-postulacion__quota-card-title">Carrito de inscripciones</h3>
          <span class="apap-postulacion__cart-count">${payableDrafts.length} borrador${payableDrafts.length === 1 ? '' : 'es'}</span>
        </div>
        <ul class="apap-postulacion__cart-list">
          ${draftLines}
        </ul>
        <div class="apap-postulacion__cart-summary">
          ${this.renderDashboardCartSummary(cart)}
        </div>
        ${actionsHtml}
      </section>
    `;
  }

  formatMoneyLabel(amount, currency, quantity = 1) {
    const value = Number(amount);
    if (!Number.isFinite(value) || !currency) return null;
    try {
      return new Intl.NumberFormat('es-PE', {
        style: 'currency',
        currency,
      }).format(value * quantity);
    } catch {
      return `${(value * quantity).toFixed(2)} ${currency}`;
    }
  }

  getDashboardCartPurchaseTotal(cart) {
    const pricing = cart?.pricing ?? {};
    const toPurchasePieza = Number(cart?.toPurchasePieza) || 0;
    const toPurchaseCampana = Number(cart?.toPurchaseCampana) || 0;
    const piezaUnit = Number(pricing.pricePiezaUnit) || 0;
    const campanaUnit = Number(pricing.priceCampanaUnit) || 0;
    return (
      (toPurchasePieza > 0 ? piezaUnit * toPurchasePieza : 0) +
      (toPurchaseCampana > 0 ? campanaUnit * toPurchaseCampana : 0)
    );
  }

  shouldSplitDashboardCart(cart) {
    return this.getDashboardCartPurchaseTotal(cart) > DASHBOARD_CART_SPLIT_THRESHOLD_PEN;
  }

  resolveCartDraftCheckoutInput(draft) {
    const quotaType = draft?.caseType === 'CAMPANA' || draft?.caseType === 'PIEZA' ? draft.caseType : null;
    const quantity = Number(draft?.slots || draft?.categoryCount) || 0;
    if (!quotaType || quantity < 1) return null;
    return { quotaType, quantity };
  }

  parseDraftCheckoutFromButton(button) {
    if (!(button instanceof Element)) return null;
    const quotaType = button.getAttribute('data-quota-type');
    const quantity = Number(button.getAttribute('data-quota-quantity'));
    if (quotaType !== 'PIEZA' && quotaType !== 'CAMPANA') return null;
    if (!Number.isFinite(quantity) || quantity < 1) return null;
    return { quotaType, quantity };
  }

  formatCartMoneyCompact(amount, currency) {
    const value = Number(amount);
    if (!Number.isFinite(value) || !currency) return null;
    try {
      return new Intl.NumberFormat('es-PE', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    } catch {
      return `${Math.round(value)} ${currency}`;
    }
  }

  renderDashboardCartLine(draft, { splitCheckout = false, canCheckout = false } = {}) {
    const cartPricing = this.state.dashboard?.cart?.pricing ?? {};
    const categoryCount = Number(draft.categoryCount || draft.slots) || 0;
    const unitLabel =
      draft.unitLabel || (draft.caseType === 'CAMPANA' ? 'campaña' : 'pieza');
    const unitPrice =
      draft.unitPrice ??
      (draft.caseType === 'CAMPANA' ? cartPricing.priceCampanaUnit : cartPricing.pricePiezaUnit);
    const unitPriceLabel =
      draft.unitPriceFormatted ||
      this.formatCartMoneyCompact(unitPrice, cartPricing.currency);
    const thumbUrl = this.api.resolveMediaUrl(draft.thumbnailUrl);
    const thumbHtml = thumbUrl
      ? `<img class="apap-postulacion__cart-thumb-img" src="${this.escapeAttr(thumbUrl)}" alt="" loading="lazy" />`
      : `<span class="apap-postulacion__cart-thumb-placeholder" aria-hidden="true"></span>`;
    const subtitleParts = [draft.brand, draft.productService]
      .map((value) => String(value ?? '').trim())
      .filter(Boolean);
    const subtitleHtml = subtitleParts.length
      ? `<p class="apap-postulacion__cart-line-subtitle">${subtitleParts.map((part) => this.escape(part)).join(' | ')}</p>`
      : '';
    const categoryPills =
      draft.categoryNames?.length > 0
        ? draft.categoryNames
            .map((name) => `<li class="apap-postulacion__cart-pill">${this.escape(name)}</li>`)
            .join('')
        : '<li class="apap-postulacion__cart-pill apap-postulacion__cart-pill--empty">Sin categorías</li>';

    const draftCheckout = this.resolveCartDraftCheckoutInput(draft);
    const showLineCheckout = canCheckout && draftCheckout && splitCheckout;
    const lineActionsHtml = showLineCheckout
        ? `<div class="apap-postulacion__cart-line-actions">
            <button type="button" class="apap-postulacion__btn apap-postulacion__btn--primary apap-postulacion__btn--sm" data-action="comprar-draft-checkout" data-quota-type="${this.escapeAttr(draftCheckout.quotaType)}" data-quota-quantity="${draftCheckout.quantity}" data-submission-id="${this.escapeAttr(draft.submissionId || '')}">Pagar individual</button>
            ${this.renderShareCheckoutButton({
              context: 'draft-cart',
              quotaType: draftCheckout.quotaType,
              quantity: draftCheckout.quantity,
              submissionId: draft.submissionId || '',
              label: 'Copiar enlace de pago',
            })}
          </div>`
        : '';

    const countBadge =
      categoryCount > 0
        ? `<span class="apap-postulacion__cart-count-badge">Categorías x ${categoryCount}</span>`
        : '';
    const unitHtml = unitPriceLabel
      ? `<span class="apap-postulacion__cart-line-price-detail">(${this.escape(unitPriceLabel)}/${this.escape(unitLabel)})</span>`
      : '';
    const lineTotalLabel = Number.isFinite(Number(draft.lineAmount))
      ? this.formatCartMoneyCompact(draft.lineAmount, cartPricing.currency)
      : draft.lineAmountFormatted;
    const totalHtml = lineTotalLabel
      ? `<span class="apap-postulacion__cart-line-price-total">${this.escape(lineTotalLabel)}</span>`
      : '';
    const priceHtml =
      countBadge || unitHtml || totalHtml
        ? `<div class="apap-postulacion__cart-line-price">${countBadge}${unitHtml}${totalHtml}</div>`
        : '';

    return `
      <li class="apap-postulacion__cart-line">
        <div class="apap-postulacion__cart-line-primary">
          <div class="apap-postulacion__cart-thumb">${thumbHtml}</div>
          <div class="apap-postulacion__cart-line-copy">
            <p class="apap-postulacion__cart-line-title">${this.escape(draft.campaignName || 'Sin título')}</p>
            ${subtitleHtml}
          </div>
        </div>
        <div class="apap-postulacion__cart-line-categories">
          <p class="apap-postulacion__cart-line-categories-label">Categorías</p>
          <ul class="apap-postulacion__cart-pills">${categoryPills}</ul>
        </div>
        ${priceHtml}
        ${lineActionsHtml}
      </li>
    `;
  }

  renderDashboardCartSummary(cart) {
    const pricing = cart.pricing ?? {};
    const { toPurchasePieza, toPurchaseCampana } = cart;
    if (!pricing.configured || (toPurchasePieza <= 0 && toPurchaseCampana <= 0)) {
      return '';
    }

    const purchaseTotal = this.formatMoneyLabel(
      String(this.getDashboardCartPurchaseTotal(cart)),
      pricing.currency,
      1,
    );

    return `
      <div class="apap-postulacion__cart-summary-copy">
        <strong>Total a pagar</strong>
        <p class="apap-postulacion__cart-summary-note">Descuentos y paquetes calculados en el checkout</p>
      </div>
      <strong class="apap-postulacion__cart-summary-amount">${purchaseTotal ? this.escape(purchaseTotal) : this.escape(pricing.totalFormatted || '—')}</strong>
    `;
  }

  renderSubmissionDetailRow(label, value) {
    const text = typeof value === 'string' ? value.trim() : '';
    return `<div class="apap-postulacion__item-detail-row"><dt>${this.escape(label)}</dt><dd>${this.escape(text || '—')}</dd></div>`;
  }

  renderSubmissionDetailListRow(label, items) {
    const listHtml =
      items?.length > 0
        ? `<ul class="apap-postulacion__review-list">${items.map((item) => `<li>${this.escape(item)}</li>`).join('')}</ul>`
        : this.escape('—');
    return `<div class="apap-postulacion__item-detail-row apap-postulacion__item-detail-row--list"><dt>${this.escape(label)}</dt><dd>${listHtml}</dd></div>`;
  }

  formatSubmissionCategoryItems(s) {
    if (s.categories?.length > 0) {
      return s.categories.map((category) =>
        category.groupName ? `${category.groupName} > ${category.name}` : category.name,
      );
    }
    if (s.category?.name) {
      const name = s.category.name;
      const groupName = s.category.groupName;
      return [groupName ? `${groupName} > ${name}` : name];
    }
    return [];
  }

  submissionHasAvailableQuota(s) {
    const slots = Number(s.creditSlots) || 0;
    if (slots < 1) return false;
    const quota = this.state.dashboard?.quota;
    if (!quota) return false;
    if (s.caseType === 'CAMPANA') {
      return (quota.campana?.available ?? 0) >= slots;
    }
    if (s.caseType === 'PIEZA') {
      return (quota.pieza?.available ?? 0) >= slots;
    }
    return false;
  }

  renderSubmissionCaseDataRows(submission) {
    const rows = [];
    if (submission.circulationDate?.trim()) {
      rows.push(this.renderReviewRow('Fecha de circulación', submission.circulationDate));
    }
    if (submission.duration?.trim()) {
      rows.push(this.renderReviewRow('Duración (AV / radio)', submission.duration));
    }
    return rows.join('');
  }

  renderSubmissionFicha(submission) {
    const categoryItems = this.formatSubmissionCategoryItems(submission);
    const caseTypeLabel =
      submission.caseType === 'PIEZA'
        ? 'Pieza'
        : submission.caseType === 'CAMPANA'
          ? 'Campaña'
          : '—';
    const creativeCredits = resolveCreativeCredits(submission);
    const media = submission.mediaAssets ?? [];
    const caseDataRows = this.renderSubmissionCaseDataRows(submission);
    const submittedAtLabel = submission.submittedAt
      ? new Date(submission.submittedAt).toLocaleDateString('es-PE', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })
      : '';
    const statusMeta = [
      submittedAtLabel ? `Enviada el ${submittedAtLabel}` : '',
      submission.editionYear ? `Edición ${submission.editionYear}` : '',
    ]
      .filter(Boolean)
      .join(' · ');

    const sections = [
      this.renderCard(
        'Estado',
        `<dl class="apap-postulacion__review-dl">
          ${this.renderReviewRow('Estado', submission.statusLabel ?? submission.status)}
          ${statusMeta ? this.renderReviewRow('Detalle', statusMeta) : ''}
        </dl>`,
      ),
    ];

    if (submission.caseType || categoryItems.length > 0) {
      sections.push(
        this.renderCard(
          'Clasificación',
          `<dl class="apap-postulacion__review-dl">
            ${this.renderReviewRow('Tipo de caso', caseTypeLabel)}
            ${this.renderReviewListRow('Categorías', categoryItems)}
          </dl>`,
        ),
      );
    }

    sections.push(
      this.renderCard(
        'Información general',
        `<dl class="apap-postulacion__review-dl">
          ${this.renderReviewRow('Nombre de la campaña / pieza', submission.campaignName)}
          ${this.renderReviewRow('Marca', submission.brand)}
          ${this.renderReviewRow('Producto / servicio', submission.productService)}
        </dl>`,
      ),
      this.renderCard(
        'Sobre el participante',
        `<dl class="apap-postulacion__review-dl">
          ${this.renderReviewRow('Nombre de la agencia', submission.participantName)}
          ${this.renderReviewRow('Razón social', submission.agencyRazonSocial)}
          ${this.renderReviewRow('RUC', submission.agencyRuc)}
          ${this.renderReviewRow('Teléfono de contacto', submission.agencyPhone)}
        </dl>`,
      ),
      this.renderCard(
        'Sobre el anunciante',
        `<dl class="apap-postulacion__review-dl">
          ${this.renderReviewRow('Nombre del cliente', submission.advertiser)}
          ${this.renderReviewRow('Correo del cliente', submission.clientEmail)}
          ${this.renderReviewRow('Número del cliente', submission.clientPhone)}
        </dl>`,
      ),
    );

    if (caseDataRows) {
      sections.push(
        this.renderCard(
          'Datos del caso',
          `<dl class="apap-postulacion__review-dl">${caseDataRows}</dl>`,
        ),
      );
    }

    if (creativeCredits) {
      sections.push(
        this.renderCard(
          'Equipo creativo',
          `<dl class="apap-postulacion__review-dl">
            ${this.renderReviewRow('Equipo', creativeCredits, { multiline: true })}
          </dl>`,
        ),
      );
    }

    sections.push(
      this.renderCard(
        'Medios',
        `<dl class="apap-postulacion__review-dl">
          ${this.renderReviewRow('Archivos adjuntos', String(media.length))}
        </dl>
        ${
          media.length
            ? `<div class="apap-postulacion__media-grid">${media.map((asset) => this.renderMediaCard({ ...asset, editable: false })).join('')}</div>`
            : '<p class="apap-postulacion__item-meta">Sin medios adjuntos.</p>'
        }`,
        { flat: true },
      ),
    );

    return `<div class="apap-postulacion__review">${sections.join('')}</div>`;
  }

  renderSubmissionItem(s) {
    const caseName = s.campaignName?.trim() || 'Sin título';
    const caseTypeLabel =
      s.caseTypeLabel ||
      (s.caseType === 'CAMPANA' ? 'Campaña' : s.caseType === 'PIEZA' ? 'Pieza' : '');
    const statusBadgeClass = this.getSubmissionStatusBadgeClass(s.status);
    const isDraft = s.status === 'DRAFT';
    const isObserved = s.status === 'OBSERVED';
    const isSubmitted = s.status === 'SUBMITTED';
    const itemStateClass = isSubmitted
      ? ' apap-postulacion__item--confirmed'
      : isSubmissionSuccessCard(s.status)
        ? ' apap-postulacion__item--success'
        : isObserved
          ? ' apap-postulacion__item--warning'
          : '';
    const completeBadge =
      isDraft && s.isComplete
        ? '<span class="apap-postulacion__badge apap-postulacion__badge--info-complete">Información completa</span>'
        : '';
    const statusBadge =
      isDraft && s.isComplete
        ? ''
        : `<span class="apap-postulacion__badge ${statusBadgeClass}">${this.escape(s.statusLabel ?? s.status)}</span>`;
    const observationBanner =
      isObserved && s.verificationNote
        ? `<div class="apap-postulacion__alert apap-postulacion__alert--warning apap-postulacion__item-observation"><strong>Observación:</strong> ${this.escape(s.verificationNote)}</div>`
        : '';
    const hasEnoughQuota = this.submissionHasAvailableQuota(s);
    const draftReviewAction =
      isDraft && s.isComplete && hasEnoughQuota
        ? `<button type="button" class="apap-postulacion__btn apap-postulacion__btn--primary apap-postulacion__btn--sm" data-action="review-submit" data-id="${this.escapeAttr(s.id)}">Revisar y confirmar participación</button>`
        : '';
    const draftEditAction = this.renderEditButton(s.id, {
      variant: isDraft && s.isComplete && hasEnoughQuota ? 'ghost' : 'primary',
    });
    const observedPrimaryAction = `<button type="button" class="apap-postulacion__btn apap-postulacion__btn--primary apap-postulacion__btn--sm" data-action="review-submit" data-id="${this.escapeAttr(s.id)}">Revisar y reenviar</button>`;

    const thumbUrl = this.api.resolveMediaUrl(s.thumbnailUrl);
    const thumbHtml = thumbUrl
      ? `<img class="apap-postulacion__cart-thumb-img" src="${this.escapeAttr(thumbUrl)}" alt="" loading="lazy" />`
      : `<span class="apap-postulacion__cart-thumb-placeholder" aria-hidden="true"></span>`;
    const subtitleParts = [s.brand, s.productService]
      .map((value) => String(value ?? '').trim())
      .filter(Boolean);
    const subtitleHtml = subtitleParts.length
      ? `<p class="apap-postulacion__cart-line-subtitle">${subtitleParts.map((part) => this.escape(part)).join(' | ')}</p>`
      : '';
    const categoryNames = this.formatSubmissionCategoryItems(s);
    const categoryPills =
      categoryNames.length > 0
        ? categoryNames.map((name) => `<li class="apap-postulacion__cart-pill">${this.escape(name)}</li>`).join('')
        : '<li class="apap-postulacion__cart-pill apap-postulacion__cart-pill--empty">Sin categorías</li>';
    const typeBadge = caseTypeLabel
      ? `<span class="apap-postulacion__badge apap-postulacion__badge--type">${this.escape(caseTypeLabel)}</span>`
      : '';
    const metaHtml =
      completeBadge || statusBadge
        ? `<div class="apap-postulacion__item-badges">${completeBadge}${statusBadge}</div>`
        : '';

    const viewAction = `<button type="button" class="apap-postulacion__btn apap-postulacion__btn--primary apap-postulacion__btn--sm" data-action="view" data-id="${this.escapeAttr(s.id)}">Ver ficha del caso</button>`;
    const actionsHtml = this.isImpersonating()
      ? `<div class="apap-postulacion__item-actions">
            <div class="apap-postulacion__item-actions-start">${viewAction}</div>
          </div>`
      : isDraft
      ? `<div class="apap-postulacion__item-actions">
            <button type="button" class="apap-postulacion__btn apap-postulacion__btn--link apap-postulacion__btn--sm" data-action="delete" data-id="${this.escapeAttr(s.id)}">Eliminar</button>
            <div class="apap-postulacion__item-actions-end">${draftReviewAction}${draftEditAction}</div>
          </div>`
      : isObserved
        ? `<div class="apap-postulacion__item-actions">
            <div class="apap-postulacion__item-actions-start">${observedPrimaryAction}</div>
            <div class="apap-postulacion__item-actions-end">${this.renderEditButton(s.id, { variant: 'ghost' })}</div>
          </div>`
        : `<div class="apap-postulacion__item-actions">
            <div class="apap-postulacion__item-actions-start">
              ${viewAction}
            </div>
          </div>`;

    return `
      <li class="apap-postulacion__item${itemStateClass}">
        <div class="apap-postulacion__cart-line apap-postulacion__item-card">
          <div class="apap-postulacion__cart-thumb">${thumbHtml}</div>
          <div class="apap-postulacion__cart-line-content">
            <div class="apap-postulacion__cart-line-primary">
              ${typeBadge}
              <p class="apap-postulacion__cart-line-title">${this.escape(caseName)}</p>
              ${subtitleHtml}
            </div>
            <div class="apap-postulacion__cart-line-categories">
              <p class="apap-postulacion__cart-line-categories-label">Categorías</p>
              <ul class="apap-postulacion__cart-pills">${categoryPills}</ul>
            </div>
            <div class="apap-postulacion__item-meta-col">${metaHtml}</div>
          </div>
        </div>
        ${observationBanner}
        ${actionsHtml}
      </li>
    `;
  }

  getSubmissionStatusBadgeClass(status) {
    switch (status) {
      case 'SUBMITTED':
        return 'apap-postulacion__badge--ingresada';
      case 'VERIFIED':
      case 'SCORED':
      case 'SHORTLIST':
      case 'SYNCED_SHORTLIST':
      case 'WINNER':
        return 'apap-postulacion__badge--verificada';
      case 'OBSERVED':
        return 'apap-postulacion__badge--observada';
      case 'DRAFT':
      default:
        return 'apap-postulacion__badge--borrador';
    }
  }

  bindDashboardActions() {
    this.querySelector('[data-action="open-account"]')?.addEventListener('click', (event) => {
      const button = event.currentTarget;
      if (!(button instanceof HTMLButtonElement)) return;
      void this.openAccount(button);
    });
    this.querySelectorAll('[data-action="create"]').forEach((btn) => {
      btn.addEventListener('click', async (event) => {
        const button = event.currentTarget;
        if (!(button instanceof HTMLButtonElement)) return;
        await this.withButtonLoading(button, async () => {
          const data = await this.api.createSubmission();
          this.wizard.submissionId = data.submission.id;
          this.wizard.step = 1;
          await this.openWizard(data.submission.id);
        }, 'Creando…');
      });
    });
    this.querySelectorAll('[data-action="comprar"]').forEach((btn) => {
      btn.addEventListener('click', async (event) => {
        const button = event.currentTarget;
        if (!(button instanceof HTMLButtonElement)) return;
        await this.withButtonLoading(button, async () => {
          this.view = 'comprar';
          this.state.comprar = await this.api.comprarCatalog();
          this.render();
        }, 'Cargando…');
      });
    });
    this.querySelectorAll('[data-action="edit"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!(btn instanceof HTMLButtonElement)) return;
        void this.withButtonLoading(btn, () => this.openWizard(btn.getAttribute('data-id')), 'Cargando…');
      });
    });
    this.querySelectorAll('[data-action="review-submit"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!(btn instanceof HTMLButtonElement)) return;
        void this.withButtonLoading(
          btn,
          () => this.openWizard(btn.getAttribute('data-id'), { step: WIZARD_STEPS.length }),
          'Cargando…',
        );
      });
    });
    this.querySelectorAll('[data-action="view"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!(btn instanceof HTMLButtonElement)) return;
        void this.withButtonLoading(btn, () => this.openView(btn.getAttribute('data-id')), 'Cargando…');
      });
    });
    this.querySelectorAll('[data-action="delete"]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        if (!(btn instanceof HTMLButtonElement)) return;
        if (!confirm('¿Eliminar este borrador?')) return;
        await this.withButtonLoading(btn, async () => {
          await this.api.deleteSubmission(btn.getAttribute('data-id'));
          await this.loadDashboard();
          this.render();
        }, 'Eliminando…');
      });
    });
    this.querySelectorAll('[data-action="comprar-shortfall-checkout"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!(btn instanceof HTMLButtonElement)) return;
        const quotaType = btn.getAttribute('data-quota-type');
        const quantity = Number(btn.getAttribute('data-quota-quantity'));
        if (!quotaType || !Number.isFinite(quantity) || quantity < 1) return;
        this.openShortfallCheckout(btn, this.buildShortfallPreview(quotaType, quantity));
      });
    });
    this.bindCheckoutActions();
  }

  bindCheckoutActions() {
    this.querySelector('[data-action="comprar-dashboard-cart"]')?.addEventListener('click', (event) => {
      const button = event.currentTarget;
      if (!(button instanceof HTMLButtonElement)) return;
      void this.withButtonLoading(button, () => this.addDashboardCartCheckout(), 'Preparando pago…');
    });
    this.querySelectorAll('[data-action="comprar-draft-checkout"]').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        const button = event.currentTarget;
        if (!(button instanceof HTMLButtonElement)) return;
        const input = this.parseDraftCheckoutFromButton(button);
        if (!input) return;
        void this.withButtonLoading(button, () => this.addDraftCartCheckout(input), 'Preparando pago…');
      });
    });
    this.querySelectorAll('[data-action="share-checkout-link"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (!(btn instanceof HTMLButtonElement)) return;
        void this.copyShortfallCheckoutLink(btn);
      });
    });
  }

  async openWizard(id, { step = 1 } = {}) {
    if (!id) return;
    if (this.isImpersonating()) {
      await this.openView(id);
      return;
    }
    this.wizard.submissionId = id;
    this.wizard.step = step;
    const [submissionData, catalogData] = await Promise.all([
      this.api.getSubmission(id),
      this.api.editionCatalog(),
    ]);
    this.wizard.submission = submissionData.submission;
    this.applyCheckoutUnits(submissionData.checkoutUnits);
    this.wizard.quota = submissionData.quota ?? {
      pieza: { available: 0, total: 0, used: 0 },
      campana: { available: 0, total: 0, used: 0 },
    };
    this.wizard.catalog = catalogData;
    this.wizard.form = this.submissionToForm(this.wizard.submission);
    this.wizard.form.acceptTerms = false;
    this.wizard.form.acceptPrivacy = false;
    this.syncPrimaryCategory();
    this.wizard.dirty = false;
    this.wizard.lastSavedPayload = JSON.stringify(
      this.formToPayload(this.wizard.form, 'autosave'),
    );
    this.view = 'wizard';
    this.render();
    if (step === WIZARD_STEPS.length) {
      this.updateMediaList();
      this.updateWizardReview();
      this.updateWizardSubmitState();
    }
  }

  markWizardDirty() {
    if (this.wizard.suppressDirty) return;
    this.wizard.dirty = true;
  }

  serializeWizardPayload() {
    this.readWizardFormFromDom();
    return JSON.stringify(this.formToPayload(this.wizard.form, 'autosave'));
  }

  async openView(id) {
    const data = await this.api.getSubmission(id);
    this.state.viewSubmission = data.submission;
    this.view = 'view';
    this.render();
  }

  submissionToForm(submission) {
    const categoryIds =
      submission.categoryIds?.length > 0
        ? submission.categoryIds
        : submission.categoryId
          ? [submission.categoryId]
          : [];
    return {
      caseType: submission.caseType ?? '',
      categoryIds,
      categoryId: categoryIds[0] ?? '',
      campaignName: submission.campaignName ?? '',
      brand: submission.brand ?? '',
      productService: submission.productService ?? '',
      participantName: submission.participantName ?? '',
      agencyRazonSocial: submission.agencyRazonSocial ?? '',
      agencyRuc: submission.agencyRuc ?? '',
      agencyPhone: submission.agencyPhone ?? '',
      advertiser: submission.advertiser ?? '',
      clientEmail: submission.clientEmail ?? '',
      clientPhone: submission.clientPhone ?? '',
      circulationDate: submission.circulationDate ?? '',
      duration: submission.duration ?? '',
      insight: submission.insight ?? '',
      execution: submission.execution ?? '',
      results: submission.results ?? '',
      creativeCredits: resolveCreativeCredits(submission),
      externalUrl: '',
      linkLabel: 'Enlace externo',
      acceptTerms: false,
      acceptPrivacy: false,
    };
  }

  formToPayload(form, intent = 'save') {
    const categoryIds = form.categoryIds ?? [];
    const payload = {
      intent,
      ...form,
      categoryIds,
      categoryId: categoryIds[0] || '',
    };
    delete payload.idea;
    delete payload.credits;
    payload.creativeCredits = parseCreativeCreditsInput(form.creativeCredits);
    if (intent === 'submit') {
      payload[ACCEPT_TERMS_FIELD] = form.acceptTerms ? 'on' : '';
      payload[ACCEPT_PRIVACY_FIELD] = form.acceptPrivacy ? 'on' : '';
    } else {
      delete payload.acceptTerms;
      delete payload.acceptPrivacy;
    }
    return payload;
  }

  getMaxCategories() {
    const fromCatalog = this.wizard.catalog?.maxCategories;
    if (typeof fromCatalog === 'number' && fromCatalog >= 1) return fromCatalog;
    return DEFAULT_MAX_CATEGORIES;
  }

  syncCaseTypeFromCategories() {
    const categories = this.wizard.catalog?.categories ?? [];
    this.wizard.form.caseType = resolveCaseTypeFromCategoryIds(
      this.wizard.form.categoryIds ?? [],
      categories,
    );
  }

  syncPrimaryCategory() {
    const { categoryIds } = this.wizard.form;
    this.wizard.form.categoryId = categoryIds?.[0] ?? '';
    this.syncCaseTypeFromCategories();
  }

  renderWizardSteps(currentStep) {
    return `
      <ol class="apap-postulacion__wizard-track">
        ${WIZARD_STEPS.map((wizardStep) => {
          const isActive = wizardStep.id === currentStep;
          const isDone = wizardStep.id < currentStep;
          const stateClass = isActive ? ' is-active' : isDone ? ' is-done' : ' is-pending';
          return `
            <li class="apap-postulacion__wizard-step-item${stateClass}">
              <button
                type="button"
                class="apap-postulacion__wizard-step-btn"
                data-action="wizard-goto"
                data-wizard-step="${wizardStep.id}"
                aria-current="${isActive ? 'step' : 'false'}"
                aria-label="Paso ${wizardStep.id}: ${this.escapeAttr(wizardStep.label)}${isDone ? ' (completado)' : isActive ? ' (actual)' : ''}"
              >
                <span class="apap-postulacion__wizard-step-circle" aria-hidden="true">
                  <span class="apap-postulacion__wizard-step-check">✓</span>
                  <span class="apap-postulacion__wizard-step-number">${wizardStep.id}</span>
                </span>
                <span class="apap-postulacion__wizard-step-label">${this.escape(wizardStep.label)}</span>
              </button>
            </li>
          `;
        }).join('')}
      </ol>
    `;
  }

  syncWizardStepper(step) {
    const stepsNav = this.querySelector('.apap-postulacion__wizard-nav');
    if (stepsNav) {
      stepsNav.innerHTML = this.renderWizardSteps(step);
    }
  }

  renderWizard() {
    const step = this.wizard.step;
    const intro = STEP_INTROS[step] ?? STEP_INTROS[1];
    return `
      <div class="apap-postulacion__panel apap-postulacion__wizard">
        <div class="apap-postulacion__toolbar">
          <div class="apap-postulacion__toolbar-head">
            <div class="apap-postulacion__toolbar-title-group">
              <button type="button" class="apap-postulacion__btn apap-postulacion__btn--primary" data-action="back-dashboard">Volver</button>
              <div>
                <h2 class="apap-postulacion__toolbar-title">Editar postulación</h2>
                <p class="apap-postulacion__item-meta">Paso ${step} de ${WIZARD_STEPS.length}</p>
              </div>
            </div>
            ${this.renderEditionCountdown()}
          </div>
        </div>
        <nav class="apap-postulacion__wizard-nav" aria-label="Progreso de la postulación">
          ${this.renderWizardSteps(step)}
        </nav>
        ${
          this.wizard.submission?.status === 'OBSERVED' && this.wizard.submission?.verificationNote
            ? `<div class="apap-postulacion__alert apap-postulacion__alert--warning"><strong>Observación del verificador:</strong> ${this.escape(this.wizard.submission.verificationNote)}</div>`
            : ''
        }
        <div class="apap-postulacion__step-intro" data-step-intro>
          <h3 class="apap-postulacion__section-title">${this.escape(intro.title)}</h3>
          <p class="apap-postulacion__section-lead">${this.escape(intro.lead)}</p>
        </div>
        <form class="apap-postulacion__form" data-form="wizard">
          <p class="apap-postulacion__required-note">Los campos marcados con <span class="apap-postulacion__required" aria-hidden="true">*</span> son obligatorios.</p>
          ${WIZARD_STEPS.map(
            (wizardStep) => `
            <div
              class="apap-postulacion__wizard-panel"
              data-wizard-panel="${wizardStep.id}"
              ${wizardStep.id !== step ? 'hidden' : ''}
            >${this.renderWizardStepContent(wizardStep.id)}</div>
          `,
          ).join('')}
        </form>
        <div data-wizard-error></div>
        <div data-wizard-status class="apap-postulacion__wizard-meta">Autoguardado cada minuto</div>
      </div>
    `;
  }

  renderWizardStepContent(step) {
    const f = this.wizard.form;
    const catalog = this.wizard.catalog;
    if (step === 1) {
      const maxCategories = this.getMaxCategories();
      const categories = catalog?.categories ?? [];
      const activeCaseType = resolveCaseTypeFromCategoryIds(f.categoryIds ?? [], categories);
      const mixedCategoryCaseTypes =
        (f.categoryIds?.length ?? 0) > 0 && !activeCaseType;
      return `
        <div class="apap-postulacion__card-stack">
          ${this.renderCard(
            'Categorías de concurso',
            `${mixedCategoryCaseTypes ? '<div class="apap-postulacion__alert apap-postulacion__alert--warning">Las categorías seleccionadas deben ser del mismo tipo (pieza o campaña).</div>' : ''}
            ${this.renderCategoryCardButtons(categories, f.categoryIds ?? [], maxCategories, activeCaseType)}`,
            { hint: 'Selecciona la subcategoría dentro de cada categoría general. El tipo de caso se define automáticamente según la categoría elegida.', required: true },
          )}
        </div>
        ${this.renderWizardFooter({ prev: false })}
      `;
    }
    if (step === 2) {
      return `
        <div class="apap-postulacion__card-stack">
          ${this.renderCard(
            'Información general',
            `<div class="apap-postulacion__field-grid apap-postulacion__field-grid--2">
              ${this.renderTextField('campaignName', 'Nombre de la campaña / pieza', f.campaignName, null, { required: true })}
              ${this.renderTextField('brand', 'Marca', f.brand, null, { required: true })}
              ${this.renderTextField('productService', 'Producto / servicio', f.productService, null, { required: true })}
            </div>`,
          )}
          ${this.renderCard(
            'Sobre el participante',
            `<div class="apap-postulacion__field-grid apap-postulacion__field-grid--2">
              ${this.renderTextField('participantName', 'Nombre de la agencia', f.participantName, null, { required: true })}
              ${this.renderTextField('agencyRazonSocial', 'Razón social', f.agencyRazonSocial, null, { required: true })}
              ${this.renderTextField('agencyRuc', 'RUC', f.agencyRuc, null, { required: true, pattern: '\\d{11}' })}
              ${this.renderTextField('agencyPhone', 'Teléfono de contacto', f.agencyPhone, null, { required: true })}
            </div>`,
          )}
          ${this.renderCard(
            'Sobre el anunciante',
            `<div class="apap-postulacion__field-grid apap-postulacion__field-grid--2">
              ${this.renderTextField('advertiser', 'Nombre del cliente', f.advertiser, null, { required: true })}
              ${this.renderTextField('clientEmail', 'Correo del cliente', f.clientEmail, null, { required: true, type: 'email' })}
              ${this.renderTextField('clientPhone', 'Número del cliente', f.clientPhone, null, { required: true, type: 'tel' })}
            </div>
            <p class="apap-postulacion__legal-note">La organización se reserva el derecho de contactar al cliente o empresa con el fin de verificar la información del anuncio.</p>`,
          )}
          ${this.renderCard(
            'Equipo creativo',
            this.renderCreativeCreditsField(f.creativeCredits ?? ''),
            { hint: 'Opcional. Reconoce al equipo detrás del caso.' },
          )}
        </div>
        ${this.renderWizardFooter({ prev: true })}
      `;
    }
    if (step === 3) {
      return `
        <div class="apap-postulacion__card-stack">
          ${this.renderCard(
            'Archivos adjuntos',
            `<div data-media-list>${this.renderMediaList()}</div>
            <div class="apap-postulacion__upload-grid">
              ${this.renderUploadTile('Imagen', 'JPG, PNG, WEBP · máx. 20 MB · 5000×5000 px', 'image/jpeg,image/png,image/webp,image/gif', 'IMAGE')}
              ${
                ENABLE_VIDEO_FILE_UPLOAD
                  ? this.renderUploadTile(
                      'Video',
                      'MP4/MOV H.264 · máx. 400 MB',
                      'video/mp4,video/quicktime,.mp4,.mov',
                      'VIDEO',
                    )
                  : ''
              }
              ${this.renderUploadTile('Audio', 'MP3, WAV · máx. 50 MB', 'audio/mpeg,audio/wav,audio/mp3', 'AUDIO')}
              ${this.renderUploadTile('PDF', 'Documento · máx. 20 MB', 'application/pdf', 'PDF')}
            </div>`,
            { hint: 'Adjunta al menos un archivo o enlace.', required: true },
          )}
          ${this.renderCard(
            'Enlaces externos',
            `<div class="apap-postulacion__field-grid apap-postulacion__field-grid--2">
              ${this.renderTextField('linkLabel', 'Etiqueta', f.linkLabel ?? 'Enlace externo')}
              ${this.renderTextField('externalUrl', 'URL', f.externalUrl ?? '', null, { placeholder: 'https://...' })}
            </div>
            <p class="apap-postulacion__item-meta">YouTube, Vimeo, Google Drive u otro enlace público.</p>
            <button type="button" class="apap-postulacion__btn apap-postulacion__btn--sm" data-action="add-external-link">Agregar enlace</button>`,
          )}
        </div>
        ${this.renderWizardFooter({ prev: true })}
      `;
    }
    return `
      <div data-wizard-review>${this.renderWizardReview()}</div>
      ${this.renderWizardFooter({
        prev: true,
        nextLabel: this.isObservedResubmission() ? 'Reenviar postulación' : 'Confirmar postulación',
        nextAction: 'submit',
        nextDisabled: !this.canSubmitWizard(),
      })}
    `;
  }

  renderMediaList() {
    const media = this.wizard.submission?.mediaAssets ?? [];
    if (!media.length) {
      return '<p class="apap-postulacion__empty-state">Aún no hay medios adjuntos. Sube un archivo o agrega un enlace.</p>';
    }
    return `<div class="apap-postulacion__media-grid">${media.map((asset) => this.renderMediaCard(asset)).join('')}</div>`;
  }

  resolveCatalogName(items, id) {
    if (!id) return '—';
    return items?.find((item) => item.id === id)?.name ?? '—';
  }

  isObservedResubmission() {
    return this.wizard.submission?.status === 'OBSERVED';
  }

  getSubmitQuotaPreview() {
    const f = this.wizard.form;
    const quota = this.wizard.quota ?? {
      pieza: { available: 0 },
      campana: { available: 0 },
    };
    const slotsToConsume = f.categoryIds?.length ?? 0;
    const caseType = f.caseType;
    const waiveQuota = this.isObservedResubmission();

    if (!caseType || (caseType !== 'PIEZA' && caseType !== 'CAMPANA') || slotsToConsume === 0) {
      return {
        quotaType: null,
        quotaLabel: null,
        slotsToConsume,
        available: 0,
        hasEnoughCredits: waiveQuota,
        shortfall: waiveQuota ? 0 : slotsToConsume,
        waivesQuota: waiveQuota,
      };
    }

    const quotaType = caseType;
    const quotaLabel = caseType === 'CAMPANA' ? 'Campaña' : 'Pieza';
    const bucket = caseType === 'CAMPANA' ? quota.campana : quota.pieza;
    const available = bucket?.available ?? 0;

    if (waiveQuota) {
      return {
        quotaType,
        quotaLabel,
        slotsToConsume: 0,
        available,
        hasEnoughCredits: true,
        shortfall: 0,
        waivesQuota: true,
      };
    }

    const hasEnoughCredits = available >= slotsToConsume;

    return {
      quotaType,
      quotaLabel,
      slotsToConsume,
      available,
      hasEnoughCredits,
      shortfall: hasEnoughCredits ? 0 : slotsToConsume - available,
      waivesQuota: false,
    };
  }

  canSubmitWizard() {
    const f = this.wizard.form;
    return (
      this.getWizardReviewIssues().length === 0 &&
      this.getSubmitQuotaPreview().hasEnoughCredits &&
      Boolean(f.acceptTerms) &&
      Boolean(f.acceptPrivacy)
    );
  }

  encodeCartPermalinkProperties(properties) {
    return btoa(JSON.stringify(properties));
  }

  buildShortfallPreview(quotaType, shortfall) {
    return {
      quotaType,
      quotaLabel: quotaType === 'CAMPANA' ? 'Campaña' : 'Pieza',
      shortfall,
    };
  }

  getShortfallCheckoutUnit(preview) {
    if (!preview?.quotaType) return null;
    return preview.quotaType === 'CAMPANA' ? this.checkoutUnits.campana : this.checkoutUnits.pieza;
  }

  getShortfallLineItemProperties(preview) {
    const unit = this.getShortfallCheckoutUnit(preview);
    if (!unit) return null;
    return {
      piece_count: String(unit.pieceCount ?? 1),
      quota_type: preview.quotaType === 'CAMPANA' ? 'campana' : 'pieza',
    };
  }

  resolveCheckoutShopUrl() {
    const fromOrigin = this.shopOrigin?.trim().replace(/\/$/, '');
    if (fromOrigin) return fromOrigin;
    const fromTheme = this.shopUrl?.trim().replace(/\/$/, '');
    if (fromTheme) return fromTheme;
    if (typeof window !== 'undefined' && window.location?.origin) {
      return window.location.origin.replace(/\/$/, '');
    }
    return '';
  }

  resolveAbsoluteCheckoutUrl(url) {
    if (!url) return url;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const origin = this.resolveCheckoutShopUrl();
    return origin ? `${origin}${url.startsWith('/') ? url : `/${url}`}` : url;
  }

  buildShortfallCheckoutUrl(preview) {
    if (!preview?.quotaType || preview.shortfall < 1) return null;

    const unit = this.getShortfallCheckoutUnit(preview);
    if (!unit?.variantNumericId) return null;

    const properties = this.getShortfallLineItemProperties(preview);
    if (!properties) return null;

    const params = new URLSearchParams();
    params.set('properties', this.encodeCartPermalinkProperties(properties));
    this.appendCheckoutBillingParams(params, this.getCheckoutBillingContext());

    return `${this.resolveCartUrl()}/${unit.variantNumericId}:${preview.shortfall}?${params.toString()}`;
  }

  buildDashboardCartCheckoutUrl() {
    const cart = this.state.dashboard?.cart;
    if (!cart?.canCheckout) return null;

    const { toPurchasePieza, toPurchaseCampana } = cart;
    if (toPurchasePieza <= 0 && toPurchaseCampana <= 0) return null;

    if (toPurchasePieza > 0 && toPurchaseCampana <= 0) {
      return this.buildShortfallCheckoutUrl(this.buildShortfallPreview('PIEZA', toPurchasePieza));
    }

    if (toPurchaseCampana > 0 && toPurchasePieza <= 0) {
      return this.buildShortfallCheckoutUrl(this.buildShortfallPreview('CAMPANA', toPurchaseCampana));
    }

    // Mixed carts must not use multi-variant permalinks (no per-line properties).
    // Share the campaña shortfall URL; Ir a pagar uses AJAX for both lines.
    return this.buildShortfallCheckoutUrl(this.buildShortfallPreview('CAMPANA', toPurchaseCampana));
  }

  async addShortfallLineToCart(preview) {
    const unit = this.getShortfallCheckoutUnit(preview);
    const properties = this.getShortfallLineItemProperties(preview);
    if (!unit?.variantNumericId || !properties || preview.shortfall < 1) {
      throw new Error('No se pudo preparar la compra de inscripciones.');
    }

    const variantId = String(unit.variantNumericId).trim();
    if (!/^\d+$/.test(variantId)) {
      throw new Error('Variant ID de inscripción inválido.');
    }

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', String(preview.shortfall));
    formData.append('properties[piece_count]', properties.piece_count);
    formData.append('properties[quota_type]', properties.quota_type);

    const response = await fetch(this.resolveCartAddUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      body: formData,
    });

    const payload = await response.json().catch(() => null);
    if (!response.ok || payload?.status) {
      throw new Error(this.parseCartErrorMessage(payload, response.status));
    }
  }

  async addShortfallCreditsAndCheckout(preview) {
    const relativeUrl = this.buildShortfallCheckoutUrl(preview);
    const url = this.resolveAbsoluteCheckoutUrl(relativeUrl);
    if (!url) {
      throw new Error('No se pudo preparar la compra de inscripciones.');
    }
    window.location.assign(url);
  }

  async addDashboardCartCheckout() {
    const cart = this.state.dashboard?.cart;
    if (!cart?.canCheckout) {
      throw new Error('No hay inscripciones pendientes de compra.');
    }

    const { toPurchasePieza, toPurchaseCampana } = cart;
    if (toPurchasePieza <= 0 && toPurchaseCampana <= 0) {
      throw new Error('No hay inscripciones pendientes de compra.');
    }

    // Storefront cartCreate attaches per-line properties so mixed PIEZA+CAMPANA
    // shortfalls credit correctly (and yield a shareable checkout URL).
    const result = await this.api.dashboardCheckout();
    const url = result?.checkoutUrl;
    if (!url) {
      throw new Error('No se pudo preparar el checkout del carrito.');
    }
    window.location.assign(url);
  }

  async createDraftCheckoutUrl(input) {
    if (!input?.quotaType || !input.quantity) {
      throw new Error('No se pudo preparar el checkout del borrador.');
    }
    const result = await this.api.comprarCheckout({
      quotaType: input.quotaType,
      quantity: input.quantity,
    });
    const url = result?.checkoutUrl;
    if (!url) {
      throw new Error('No se pudo preparar el checkout del borrador.');
    }
    return url;
  }

  async addDraftCartCheckout(input) {
    const url = await this.createDraftCheckoutUrl(input);
    window.location.assign(url);
  }

  openShortfallCheckout(button, preview) {
    void this.withButtonLoading(
      button,
      () => this.addShortfallCreditsAndCheckout(preview),
      'Preparando pago…',
    );
  }

  async copyShortfallCheckoutLink(button) {
    const checkoutContext =
      button instanceof Element ? button.getAttribute('data-checkout-context') : null;

    const markCopied = () => {
      if (!(button instanceof HTMLButtonElement)) return;
      const label = button.querySelector('[data-share-label]');
      const original = button.dataset.originalShareLabel || label?.textContent || button.textContent;
      button.dataset.originalShareLabel = original;
      if (label) label.textContent = 'Enlace copiado';
      else button.textContent = 'Enlace copiado';
      button.disabled = true;
      window.setTimeout(() => {
        if (label) label.textContent = button.dataset.originalShareLabel || original;
        else button.textContent = button.dataset.originalShareLabel || original;
        button.disabled = false;
      }, 2500);
    };

    let url = null;
    try {
      if (checkoutContext === 'dashboard-cart') {
        this.setButtonLoading(button, true, 'Copiando…');
        const result = await this.api.dashboardCheckout();
        url = result?.checkoutUrl ?? null;
        this.setButtonLoading(button, false);
      } else if (checkoutContext === 'draft-cart') {
        const input = this.parseDraftCheckoutFromButton(button);
        if (!input) return;
        this.setButtonLoading(button, true, 'Copiando…');
        url = await this.createDraftCheckoutUrl(input);
        this.setButtonLoading(button, false);
      } else {
        const relativeUrl = this.buildShortfallCheckoutUrl(this.getSubmitQuotaPreview());
        url = this.resolveAbsoluteCheckoutUrl(relativeUrl);
      }
      if (!url) return;
      await navigator.clipboard.writeText(url);
      markCopied();
    } catch (error) {
      this.setButtonLoading(button, false);
      if (url) {
        window.prompt('Copia este enlace de pago:', url);
        return;
      }
      if (error instanceof Error) {
        const selector = this.view === 'wizard' ? '[data-wizard-error]' : '[data-dashboard-error]';
        this.showInlineError(selector, error.message);
        return;
      }
      throw error;
    }
  }

  renderSubmitQuotaCard() {
    const preview = this.getSubmitQuotaPreview();
    const { slotsToConsume } = preview;
    const isObservedResubmission = preview.waivesQuota;
    const paymentUiEnabled = this.isQuotaPaymentUiEnabled();

    let messageHtml = '';
    if (isObservedResubmission) {
      messageHtml =
        '<div class="apap-postulacion__alert apap-postulacion__alert--info">Esta postulación ya fue ingresada. Al reenviar no se consumirán inscripciones adicionales y volverá a revisión del verificador para su validación.</div>';
    } else if (!preview.quotaLabel || slotsToConsume === 0) {
      messageHtml =
        '<div class="apap-postulacion__alert apap-postulacion__alert--warning">Completa el tipo de caso y las categorías para calcular las inscripciones necesarias.</div>';
    } else if (preview.hasEnoughCredits) {
      messageHtml = `<div class="apap-postulacion__alert apap-postulacion__alert--info">Al enviar se descontarán ${preview.slotsToConsume} inscripción${preview.slotsToConsume === 1 ? '' : 'es'} de ${preview.quotaLabel.toLowerCase()} de tu bolsa. Esta acción no se puede deshacer.</div>`;
    } else if (paymentUiEnabled) {
      const canCheckout = this.isThemeCheckoutConfigured() && preview.quotaType && preview.shortfall >= 1;
      const buyLabel = `Comprar ${preview.shortfall} inscripción${preview.shortfall === 1 ? '' : 'es'} de ${this.escape(preview.quotaLabel.toLowerCase())}`;
      messageHtml = `
        <div class="apap-postulacion__alert apap-postulacion__alert--error">Para poder inscribir tu caso te falta pagar la inscripción. Puedes comprar tus inscripciones en el siguiente enlace o compartir el enlace de pago; una vez pagado se acredita automáticamente.</div>
        ${
          canCheckout
            ? `<div class="apap-postulacion__quota-actions">
                <button type="button" class="apap-postulacion__btn apap-postulacion__btn--primary" data-action="comprar-shortfall-checkout" data-quota-type="${this.escapeAttr(preview.quotaType)}" data-quota-quantity="${preview.shortfall}">${buyLabel}</button>
                <button type="button" class="apap-postulacion__btn apap-postulacion__btn--ghost" data-action="share-checkout-link">Compartir enlace de pago</button>
              </div>`
            : this.isThemeCheckoutConfigured()
              ? '<div class="apap-postulacion__alert apap-postulacion__alert--warning">Completa el tipo de caso y las categorías para calcular el checkout.</div>'
              : '<div class="apap-postulacion__alert apap-postulacion__alert--warning">Configura los variant ID de inscripción pieza y campaña en la section del theme.</div>'
        }
      `;
    } else {
      messageHtml = `
        <div class="apap-postulacion__alert apap-postulacion__alert--error">
          No puedes confirmar esta postulación porque no tienes inscripciones disponibles. Debes pagar las postulaciones desde la
          <button type="button" class="apap-postulacion__inline-link" data-action="back-dashboard">página de inicio</button>;
          una vez pagado se acredita automáticamente.
        </div>
      `;
    }

    return `
      <section class="apap-postulacion__quota-card" aria-labelledby="submit-quota-heading">
        <div class="apap-postulacion__quota-card-header">
          <h3 id="submit-quota-heading" class="apap-postulacion__quota-card-title">${isObservedResubmission ? 'Reenvío por observación' : paymentUiEnabled || preview.hasEnoughCredits ? 'Pagar inscripción' : 'Inscripciones insuficientes'}</h3>
          ${!isObservedResubmission && preview.quotaLabel ? `<span class="apap-postulacion__badge ${preview.hasEnoughCredits ? 'apap-postulacion__badge--ingresada' : 'apap-postulacion__badge--borrador'}">${this.escape(preview.quotaLabel)}</span>` : ''}
        </div>
        ${messageHtml}
      </section>
    `;
  }

  renderLegalLink(href, label) {
    if (!href) return this.escape(label);
    return `<a href="${this.escapeAttr(href)}" target="_blank" rel="noopener noreferrer">${this.escape(label)}</a>`;
  }

  renderLegalAcceptance() {
    const f = this.wizard.form;
    const links = this.legalLinks ?? {};
    return `
      <section class="apap-postulacion__legal-acceptance" aria-labelledby="legal-acceptance-heading">
        <h3 id="legal-acceptance-heading" class="apap-postulacion__legal-acceptance-title">Declaraciones obligatorias</h3>
        <div class="apap-postulacion__legal-acceptance-options">
          <label class="apap-postulacion__legal-acceptance-option">
            <input type="checkbox" data-bind-legal="${ACCEPT_TERMS_FIELD}" ${f.acceptTerms ? 'checked' : ''} required />
            <span>
              Acepto los ${this.renderLegalLink(links.termsUrl, 'términos y condiciones')} y las ${this.renderLegalLink(links.basesUrl, 'bases del Premio APAP')}.
              <span class="apap-postulacion__required" aria-hidden="true"> *</span>
            </span>
          </label>
          <label class="apap-postulacion__legal-acceptance-option">
            <input type="checkbox" data-bind-legal="${ACCEPT_PRIVACY_FIELD}" ${f.acceptPrivacy ? 'checked' : ''} required />
            <span>
              Acepto las ${this.renderLegalLink(links.privacyUrl, 'políticas de privacidad')}.
              <span class="apap-postulacion__required" aria-hidden="true"> *</span>
            </span>
          </label>
        </div>
      </section>
    `;
  }

  getWizardReviewIssues() {
    const f = this.wizard.form;
    const issues = [];
    if (!f.categoryIds?.length) issues.push('Al menos una categoría de concurso');
    if (f.categoryIds?.length && !f.caseType) {
      issues.push('Categorías compatibles del mismo tipo (pieza o campaña)');
    }
    if (!f.campaignName?.trim()) issues.push('Nombre de la campaña / pieza');
    if (!f.brand?.trim()) issues.push('Marca');
    if (!f.productService?.trim()) issues.push('Producto / servicio');
    if (!f.participantName?.trim()) issues.push('Nombre de la agencia');
    if (!f.agencyRazonSocial?.trim()) issues.push('Razón social');
    if (!isValidRuc(f.agencyRuc)) issues.push('RUC (11 dígitos)');
    if (!f.agencyPhone?.trim()) issues.push('Teléfono de contacto');
    if (!f.advertiser?.trim()) issues.push('Nombre del cliente');
    if (!isValidEmail(f.clientEmail)) issues.push('Correo del cliente');
    if (!f.clientPhone?.trim()) issues.push('Número del cliente');
    if (!(this.wizard.submission?.mediaAssets?.length ?? 0)) {
      issues.push('Al menos un medio adjunto');
    }
    return issues;
  }

  renderWizardReview() {
    const f = this.wizard.form;
    const catalog = this.wizard.catalog;
    const issues = this.getWizardReviewIssues();
    const preview = this.getSubmitQuotaPreview();
    const categoryItems = (f.categoryIds ?? [])
      .map((id) => {
        const category = catalog?.categories?.find((item) => item.id === id);
        if (!category) return null;
        return category.groupName
          ? `${category.groupName} > ${category.name}`
          : category.name;
      })
      .filter(Boolean);
    const caseTypeLabel = f.caseType === 'PIEZA' ? 'Pieza' : f.caseType === 'CAMPANA' ? 'Campaña' : '—';
    const mediaCount = this.wizard.submission?.mediaAssets?.length ?? 0;

    return `
      ${
        issues.length
          ? `<div class="apap-postulacion__alert apap-postulacion__alert--error">Completa ${issues.length} campo(s) obligatorio(s): ${issues.map((issue) => this.escape(issue)).join(', ')}.</div>`
          : preview.hasEnoughCredits
            ? '<div class="apap-postulacion__alert apap-postulacion__alert--success">Todos los campos obligatorios están completos. Puedes enviar tu postulación.</div>'
            : ''
      }
      <div class="apap-postulacion__review">
        ${this.renderCard(
          'Categorías',
          `<dl class="apap-postulacion__review-dl">
            ${this.renderReviewRow('Tipo de caso', caseTypeLabel)}
            ${this.renderReviewListRow('Categorías', categoryItems)}
          </dl>`,
        )}
        ${this.renderCard(
          'Información general',
          `<dl class="apap-postulacion__review-dl">
            ${this.renderReviewRow('Nombre de la campaña / pieza', f.campaignName)}
            ${this.renderReviewRow('Marca', f.brand)}
            ${this.renderReviewRow('Producto / servicio', f.productService)}
          </dl>`,
        )}
        ${this.renderCard(
          'Sobre el participante',
          `<dl class="apap-postulacion__review-dl">
            ${this.renderReviewRow('Nombre de la agencia', f.participantName)}
            ${this.renderReviewRow('Razón social', f.agencyRazonSocial)}
            ${this.renderReviewRow('RUC', f.agencyRuc)}
            ${this.renderReviewRow('Teléfono de contacto', f.agencyPhone)}
          </dl>`,
        )}
        ${this.renderCard(
          'Sobre el anunciante',
          `<dl class="apap-postulacion__review-dl">
            ${this.renderReviewRow('Nombre del cliente', f.advertiser)}
            ${this.renderReviewRow('Correo del cliente', f.clientEmail)}
            ${this.renderReviewRow('Número del cliente', f.clientPhone)}
          </dl>`,
        )}
        ${this.renderCard(
          'Equipo creativo',
          `<dl class="apap-postulacion__review-dl">
            ${this.renderReviewRow('Equipo', f.creativeCredits, { multiline: true })}
          </dl>`,
        )}
        ${this.renderCard(
          'Medios',
          `<dl class="apap-postulacion__review-dl">
            ${this.renderReviewRow('Archivos adjuntos', String(mediaCount))}
          </dl>
          ${this.renderMediaList()}`,
        )}
      </div>
      ${this.renderLegalAcceptance()}
      ${this.renderSubmitQuotaCard()}
    `;
  }

  updateMediaList() {
    const container = this.querySelector('[data-media-list]');
    if (container) container.innerHTML = this.renderMediaList();
  }

  updateWizardReview() {
    this.readWizardFormFromDom();
    const container = this.querySelector('[data-wizard-review]');
    if (container) container.innerHTML = this.renderWizardReview();
    this.updateWizardSubmitState();
  }

  updateWizardSubmitState() {
    const submitButton = this.querySelector('[data-action="submit"]');
    if (!(submitButton instanceof HTMLButtonElement)) return;
    submitButton.disabled = !this.canSubmitWizard();
  }

  updateCategorySelectionUI() {
    const maxCategories = this.getMaxCategories();
    const categoryInputs = this.querySelectorAll('[data-bind-category]');
    const selectedIdsFromDom = [];
    categoryInputs.forEach((input) => {
      if (/** @type {HTMLInputElement} */ (input).checked) {
        selectedIdsFromDom.push(/** @type {HTMLInputElement} */ (input).value);
      }
    });
    const selectedIds =
      categoryInputs.length > 0
        ? selectedIdsFromDom
        : (this.wizard.form.categoryIds ?? []);
    const selectedCount = selectedIds.length;
    const categories = this.wizard.catalog?.categories ?? [];
    const activeCaseType = resolveCaseTypeFromCategoryIds(selectedIds, categories);
    const caseTypeLabel =
      activeCaseType === 'CAMPANA' ? 'Campaña' : activeCaseType === 'PIEZA' ? 'Pieza' : '';
    const counter = this.querySelector('[data-category-counter]');
    if (counter) counter.textContent = `${selectedCount} / ${maxCategories}`;
    const caseTypeHint = this.querySelector('[data-case-type-hint]');
    if (caseTypeHint instanceof HTMLElement) {
      if (caseTypeLabel) {
        caseTypeHint.hidden = false;
        caseTypeHint.textContent = `Tipo de caso: ${caseTypeLabel}`;
      } else {
        caseTypeHint.hidden = true;
        caseTypeHint.textContent = '';
      }
    }

    this.querySelectorAll('[data-bind-category]').forEach((input) => {
      const checkbox = /** @type {HTMLInputElement} */ (input);
      const card = checkbox.closest('.apap-postulacion__category-card');
      const checked = checkbox.checked;
      const categoryCaseType = checkbox.getAttribute('data-case-type') || 'PIEZA';
      const typeMismatch = Boolean(activeCaseType && categoryCaseType !== activeCaseType);
      if (!checked) {
        checkbox.disabled = selectedCount >= maxCategories || typeMismatch;
      } else {
        checkbox.disabled = false;
      }
      if (!card) return;
      card.classList.toggle('apap-postulacion__category-card--selected', checked);
      card.classList.toggle(
        'apap-postulacion__category-card--disabled',
        !checked && (selectedCount >= maxCategories || typeMismatch),
      );
      let icon = card.querySelector('.apap-postulacion__category-card-icon');
      if (checked && !icon) {
        icon = document.createElement('span');
        icon.className = 'apap-postulacion__category-card-icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.textContent = '✓';
        card.appendChild(icon);
      } else if (!checked && icon) {
        icon.remove();
      }
      let selectButton = card.querySelector('.apap-postulacion__category-card-select');
      if (!checked && !selectButton) {
        selectButton = document.createElement('span');
        selectButton.className = 'apap-postulacion__category-card-select';
        selectButton.setAttribute('aria-hidden', 'true');
        selectButton.textContent = 'Seleccionar';
        card.appendChild(selectButton);
      } else if (checked && selectButton) {
        selectButton.remove();
      }
    });
  }

  showWizardStep(step, { save = true } = {}) {
    if (step < 1 || step > WIZARD_STEPS.length || step === this.wizard.step) return Promise.resolve();
    return this.applyWizardStep(step, { save });
  }

  scrollWizardToTop() {
    const wizard = this.querySelector('.apap-postulacion__wizard');
    if (wizard instanceof HTMLElement) {
      wizard.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async applyWizardStep(step, { save = true } = {}) {
    if (save) {
      this.readWizardFormFromDom();
      void this.flushPendingSave(false).catch((error) => {
        this.showInlineError('[data-wizard-error]', error.message);
      });
    } else {
      this.readWizardFormFromDom();
    }
    this.wizard.step = step;
    this.syncWizardStepper(step);
    this.querySelectorAll('[data-wizard-panel]').forEach((panel) => {
      panel.hidden = Number(panel.getAttribute('data-wizard-panel')) !== step;
    });
    const intro = STEP_INTROS[step];
    const introEl = this.querySelector('[data-step-intro]');
    if (intro && introEl) {
      introEl.innerHTML = `
        <h3 class="apap-postulacion__section-title">${this.escape(intro.title)}</h3>
        <p class="apap-postulacion__section-lead">${this.escape(intro.lead)}</p>
      `;
    }
    const stepMeta = this.querySelector('.apap-postulacion__toolbar .apap-postulacion__item-meta');
    if (stepMeta) stepMeta.textContent = `Paso ${step} de ${WIZARD_STEPS.length}`;
    if (step === 4) {
      this.updateMediaList();
      this.updateWizardReview();
      this.updateWizardSubmitState();
      this.scrollWizardToTop();
      return;
    }
    if (step === 3) this.updateMediaList();
    const status = this.querySelector('[data-wizard-status]');
    if (status && save) status.textContent = '';
    this.scrollWizardToTop();
  }

  startWizardAutosave() {
    this.stopWizardAutosave();
    this._autosaveTimer = window.setInterval(() => {
      if (this.view !== 'wizard') return;
      void this.saveWizardIfDirty(false).catch(() => {});
    }, AUTOSAVE_MS);
  }

  stopWizardAutosave() {
    if (this._autosaveTimer) {
      window.clearInterval(this._autosaveTimer);
      this._autosaveTimer = null;
    }
  }

  formatSavedAt(iso) {
    return new Date(iso).toLocaleTimeString('es-PE', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  renderCard(title, body, { hint, flat, required = false } = {}) {
    const titleMarkup = title
      ? `${this.escape(title)}${required ? '<span class="apap-postulacion__required" aria-hidden="true">*</span>' : ''}`
      : '';
    return `
      <section class="apap-postulacion__card${flat ? ' apap-postulacion__card--flat' : ''}">
        ${
          title
            ? `<header class="apap-postulacion__card-header">
            <h3 class="apap-postulacion__card-title">${titleMarkup}</h3>
            ${hint ? `<p class="apap-postulacion__card-hint">${this.escape(hint)}</p>` : ''}
          </header>`
            : ''
        }
        <div class="apap-postulacion__card-body">${body}</div>
      </section>
    `;
  }

  renderFieldLabel(label, { required = false } = {}) {
    return `<label class="apap-postulacion__label">${this.escape(label)}${
      required ? '<span class="apap-postulacion__required" aria-hidden="true">*</span>' : ''
    }</label>`;
  }

  renderWizardFooter({ prev = true, nextLabel = 'Siguiente', nextAction = 'wizard-next', nextDisabled = false } = {}) {
    return `
      <footer class="apap-postulacion__wizard-footer">
        <div class="apap-postulacion__wizard-footer-actions">
          ${
            prev
              ? '<button type="button" class="apap-postulacion__btn apap-postulacion__btn--ghost" data-action="wizard-prev">Anterior</button>'
              : '<span aria-hidden="true"></span>'
          }
          <button type="button" class="apap-postulacion__btn apap-postulacion__btn--primary" data-action="${this.escapeAttr(nextAction)}" ${nextDisabled ? 'disabled' : ''}>${this.escape(nextLabel)}</button>
        </div>
      </footer>
    `;
  }

  renderUploadTile(label, hint, accept, mediaType) {
    return `
      <label class="apap-postulacion__upload-tile" data-upload-tile="${this.escapeAttr(mediaType)}">
        <span class="apap-postulacion__upload-tile-label">${this.escape(label)}</span>
        <span class="apap-postulacion__upload-tile-hint">${this.escape(hint)}</span>
        <span class="apap-postulacion__upload-trigger">Seleccionar archivo</span>
        <div class="apap-postulacion__upload-status" hidden>
          <span class="apap-postulacion__upload-status-label">Subiendo…</span>
          <div
            class="apap-postulacion__upload-progress"
            role="progressbar"
            aria-valuemin="0"
            aria-valuemax="100"
            aria-valuenow="0"
          >
            <div class="apap-postulacion__upload-progress-bar"></div>
          </div>
        </div>
        <input type="file" class="apap-postulacion__upload-input" accept="${this.escapeAttr(accept)}" data-upload="${this.escapeAttr(mediaType)}">
      </label>
    `;
  }

  renderReviewRow(label, value, { multiline = false } = {}) {
    const content = multiline
      ? `<span class="apap-postulacion__review-multiline">${this.escape(value || '—').replace(/\n/g, '<br>')}</span>`
      : this.escape(value || '—');
    return `<div class="apap-postulacion__review-row"><dt>${this.escape(label)}</dt><dd>${content}</dd></div>`;
  }

  renderCreativeCreditsField(value) {
    const length = (value ?? '').length;
    return `
      <div class="apap-postulacion__field">
        <textarea
          class="apap-postulacion__textarea"
          name="creativeCredits"
          data-bind="creativeCredits"
          maxlength="${MAX_CREATIVE_CREDITS_LENGTH}"
          rows="5"
          placeholder="${this.escapeAttr(CREATIVE_CREDITS_PLACEHOLDER)}"
        >${this.escape(value ?? '')}</textarea>
        <p class="apap-postulacion__char-counter" data-char-counter="creativeCredits" aria-live="polite">${length} / ${MAX_CREATIVE_CREDITS_LENGTH}</p>
      </div>
    `;
  }

  updateCharCounter(name, length) {
    const counter = this.querySelector(`[data-char-counter="${name}"]`);
    if (counter) {
      counter.textContent = `${length} / ${MAX_CREATIVE_CREDITS_LENGTH}`;
    }
  }

  renderReviewListRow(label, items) {
    const listHtml =
      items?.length > 0
        ? `<ul class="apap-postulacion__review-list">${items.map((item) => `<li>${this.escape(item)}</li>`).join('')}</ul>`
        : this.escape('—');
    return `<div class="apap-postulacion__review-row"><dt>${this.escape(label)}</dt><dd>${listHtml}</dd></div>`;
  }

  groupCategoriesByDiscipline(categories) {
    const groups = [];
    for (const category of categories ?? []) {
      const key = category.groupSlug || category.groupName || category.id;
      let group = groups.find((entry) => entry.key === key);
      if (!group) {
        group = {
          key,
          label: category.groupCode
            ? `${category.groupCode}. ${category.groupName}`
            : category.groupName || 'Otras categorías',
          description: category.groupDescription || '',
          categories: [],
        };
        groups.push(group);
      }
      group.categories.push(category);
    }
    return groups;
  }

  renderCategoryCardButtons(categories, selectedIds, maxCategories, activeCaseType = '') {
    const selectedCount = selectedIds.length;
    const caseTypeLabel =
      activeCaseType === 'CAMPANA' ? 'Campaña' : activeCaseType === 'PIEZA' ? 'Pieza' : '';
    const renderCard = (category) => {
      const checked = selectedIds.includes(category.id);
      const disabled =
        !checked &&
        (selectedCount >= maxCategories ||
          !categoryMatchesActiveCaseType(category, activeCaseType));
      return `
        <label class="apap-postulacion__category-card${checked ? ' apap-postulacion__category-card--selected' : ''}${disabled ? ' apap-postulacion__category-card--disabled' : ''}">
          <input
            type="checkbox"
            class="apap-postulacion__category-input"
            name="categoryIds"
            value="${this.escapeAttr(category.id)}"
            data-bind-category
            data-case-type="${this.escapeAttr(category.caseType || 'PIEZA')}"
            ${checked ? 'checked' : ''}
            ${disabled ? 'disabled' : ''}
          />
          <span class="apap-postulacion__category-card-body">
            <span class="apap-postulacion__category-card-text">${this.escape(category.name)}</span>
            ${category.description ? `<span class="apap-postulacion__category-card-description">${this.escape(category.description)}</span>` : ''}
          </span>
          ${checked ? '<span class="apap-postulacion__category-card-icon" aria-hidden="true">✓</span>' : '<span class="apap-postulacion__category-card-select" aria-hidden="true">Seleccionar</span>'}
        </label>
      `;
    };
    const groups = this.groupCategoriesByDiscipline(categories);
    return `
      <fieldset class="apap-postulacion__category-fieldset">
        <legend class="apap-postulacion__label">
          Selecciona hasta ${maxCategories} subcategorías
          <span class="apap-postulacion__counter" data-category-counter>${selectedCount} / ${maxCategories}</span>
        </legend>
        ${caseTypeLabel ? `<p class="apap-postulacion__case-type-hint" data-case-type-hint>Tipo de caso: ${this.escape(caseTypeLabel)}</p>` : '<p class="apap-postulacion__case-type-hint" data-case-type-hint hidden></p>'}
        <div role="group" aria-label="Categorías de concurso">
          ${groups
            .map(
              (group) => `
                <div class="apap-postulacion__category-group">
                  <p class="apap-postulacion__category-group-title">${this.escape(group.label)}</p>
                  ${group.description ? `<p class="apap-postulacion__category-group-description">${this.escape(group.description)}</p>` : ''}
                  <div class="apap-postulacion__category-grid">
                    ${group.categories.map(renderCard).join('')}
                  </div>
                </div>
              `,
            )
            .join('')}
        </div>
      </fieldset>
    `;
  }

  renderTextField(name, label, value, _group = null, { required = false, pattern = null, placeholder = null, type = 'text', hint = null, disabled = false } = {}) {
    const bind = `data-bind="${name}"`;
    const attrs = [
      required && !disabled ? 'required' : '',
      pattern ? `pattern="${pattern}"` : '',
      placeholder ? `placeholder="${this.escapeAttr(placeholder)}"` : '',
      type && type !== 'text' ? `type="${type}"` : '',
      disabled ? 'disabled' : '',
    ]
      .filter(Boolean)
      .join(' ');
    const hintHtml = hint
      ? `<p class="apap-postulacion__field-hint">${this.escape(hint)}</p>`
      : '';
    return `<div class="apap-postulacion__field">${this.renderFieldLabel(label, { required })}<input class="apap-postulacion__input" name="${name}" ${bind} value="${this.escapeAttr(value ?? '')}" ${attrs}>${hintHtml}</div>`;
  }

  renderTextarea(name, label, value, { required = false } = {}) {
    const attrs = required ? 'required' : '';
    return `<div class="apap-postulacion__field">${this.renderFieldLabel(label, { required })}<textarea class="apap-postulacion__textarea" name="${name}" data-bind="${name}" ${attrs}>${this.escape(value ?? '')}</textarea></div>`;
  }

  renderDocumentIcon() {
    return `<svg class="apap-postulacion__media-type-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"></path>
      <path d="M14 2v6h6" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"></path>
      <path d="M8 13h8M8 17h5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"></path>
    </svg>`;
  }

  renderWebsiteIcon() {
    return `<svg class="apap-postulacion__media-type-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.5"></circle>
      <path d="M3 12h18M12 3c2.5 2.8 4 6.1 4 9s-1.5 6.2-4 9M12 3c-2.5 2.8-4 6.1-4 9s1.5 6.2 4 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"></path>
    </svg>`;
  }

  renderExternalLinkPreview(asset, href) {
    return `<a class="apap-postulacion__media-file-link" href="${this.escapeAttr(href)}" target="_blank" rel="noopener noreferrer">
      ${this.renderWebsiteIcon()}
      <span class="apap-postulacion__media-file-label">${this.escape(asset.label ?? asset.externalUrl ?? 'Sitio web')}</span>
    </a>`;
  }

  renderMediaCard(asset) {
    const url = this.api.resolveMediaUrl(asset.downloadUrl);
    let previewInner = '';
    let previewClass = 'apap-postulacion__media-card-preview';
    if (asset.type === 'IMAGE') {
      previewInner = url
        ? `<img src="${this.escapeAttr(url)}" alt="${this.escapeAttr(asset.fileName ?? asset.label ?? 'Imagen')}" loading="lazy">`
        : '<p class="apap-postulacion__item-meta">Vista previa no disponible</p>';
    } else if (asset.type === 'VIDEO' && url) {
      previewInner = `<video controls preload="metadata" src="${this.escapeAttr(url)}"></video>`;
    } else if (asset.type === 'AUDIO' && url) {
      previewClass += ' apap-postulacion__media-card-preview--audio';
      previewInner = `<audio controls preload="metadata" src="${this.escapeAttr(url)}"></audio>`;
    } else if (asset.type === 'PDF' && url) {
      previewClass += ' apap-postulacion__media-card-preview--file';
      previewInner = `<a class="apap-postulacion__media-file-link" href="${this.escapeAttr(url)}" target="_blank" rel="noopener noreferrer">
        ${this.renderDocumentIcon()}
        <span class="apap-postulacion__media-file-label">${this.escape(asset.fileName ?? asset.label ?? 'PDF')}</span>
      </a>`;
    } else if (asset.type === 'VIDEO_URL' && asset.embedUrl) {
      previewInner = `<iframe src="${this.escapeAttr(asset.embedUrl)}" title="${this.escapeAttr(asset.label ?? 'Video')}" allowfullscreen loading="lazy"></iframe>`;
    } else if (asset.type === 'EXTERNAL_LINK') {
      previewClass += ' apap-postulacion__media-card-preview--file';
      previewInner = this.renderExternalLinkPreview(asset, asset.externalUrl ?? url ?? '#');
    } else if (asset.type === 'VIDEO_URL') {
      previewClass += ' apap-postulacion__media-card-preview--file';
      previewInner = this.renderExternalLinkPreview(asset, asset.externalUrl ?? url ?? '#');
    } else if (url) {
      previewInner = `<a href="${this.escapeAttr(url)}" target="_blank" rel="noopener noreferrer">${this.escape(asset.fileName ?? 'Descargar')}</a>`;
    } else {
      previewInner = `<p class="apap-postulacion__item-meta">${this.escape(asset.type)}</p>`;
    }
    const typeLabel =
      asset.type === 'VIDEO' || asset.type === 'VIDEO_URL' || asset.type === 'EXTERNAL_LINK'
        ? asset.type === 'VIDEO'
          ? 'Video'
          : asset.type === 'EXTERNAL_LINK'
            ? 'Sitio web'
            : 'Enlace'
        : asset.type === 'AUDIO'
          ? 'Audio'
          : asset.type;
    return `
      <article class="apap-postulacion__media-card">
        <div class="${previewClass}">${previewInner}</div>
        <div class="apap-postulacion__media-card-meta">
          <span class="apap-postulacion__media-type">${this.escape(typeLabel)}</span>
          <span class="apap-postulacion__media-name">${this.escape(asset.label ?? asset.fileName ?? asset.type)}</span>
        </div>
        ${
          asset.editable !== false
            ? `<button type="button" class="apap-postulacion__btn apap-postulacion__btn--sm apap-postulacion__btn--danger" data-delete-media="${this.escapeAttr(asset.id)}">Eliminar</button>`
            : ''
        }
      </article>
    `;
  }

  bindWizardForm() {
    const root = this.querySelector('.apap-postulacion__wizard');
    if (!root || root.dataset.bound === 'true') return;
    root.dataset.bound = 'true';

    root.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target.closest('[data-action], [data-delete-media]') : null;
      if (!target || !root.contains(target)) return;
      if (!(target instanceof HTMLButtonElement)) return;

      if (target.hasAttribute('data-delete-media')) {
        void this.withButtonLoading(target, () => this.deleteWizardMedia(target.getAttribute('data-delete-media')), 'Eliminando…');
        return;
      }

      const action = target.getAttribute('data-action');
      if (action === 'back-dashboard') {
        if (target.classList.contains('apap-postulacion__inline-link')) {
          void this.exitWizardToDashboard();
          return;
        }
        void this.withButtonLoading(target, () => this.exitWizardToDashboard(), 'Guardando…');
        return;
      }
      if (action === 'comprar') {
        void this.withButtonLoading(target, async () => {
          this.view = 'comprar';
          this.state.comprar = await this.api.comprarCatalog();
          this.render();
        }, 'Cargando…');
        return;
      }
      if (action === 'comprar-shortfall') {
        const quotaType = target.getAttribute('data-quota-type');
        const quantity = Number(target.getAttribute('data-quota-quantity'));
        if (!quotaType || !Number.isFinite(quantity) || quantity < 1) return;
        this.openShortfallCheckout(target, this.buildShortfallPreview(quotaType, quantity));
        return;
      }
      if (action === 'comprar-shortfall-checkout') {
        const quotaType = target.getAttribute('data-quota-type');
        const quantity = Number(target.getAttribute('data-quota-quantity'));
        if (!quotaType || !Number.isFinite(quantity) || quantity < 1) return;
        this.openShortfallCheckout(target, this.buildShortfallPreview(quotaType, quantity));
        return;
      }
      if (action === 'share-checkout-link') {
        void this.copyShortfallCheckoutLink(target);
        return;
      }
      if (action === 'wizard-prev') {
        void this.withButtonLoading(target, () => this.showWizardStep(this.wizard.step - 1), 'Guardando…');
        return;
      }
      if (action === 'wizard-next') {
        void this.withButtonLoading(target, () => this.showWizardStep(this.wizard.step + 1), 'Guardando…');
        return;
      }
      if (action === 'wizard-goto') {
        const step = Number(target.getAttribute('data-wizard-step'));
        if (Number.isFinite(step)) {
          void this.withButtonLoading(target, () => this.showWizardStep(step), 'Guardando…');
        }
        return;
      }
      if (action === 'submit') {
        void this.withButtonLoading(target, () => this.submitWizard(), 'Enviando…');
        return;
      }
      if (action === 'add-external-link') {
        void this.withButtonLoading(target, () => this.addWizardExternalLink(), 'Agregando…');
        return;
      }
    });

    root.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.matches('[data-bind-category]')) {
        this.handleCategoryCheckboxChange(/** @type {HTMLInputElement} */ (target));
        return;
      }
      if (target.matches('[data-bind-legal]')) {
        this.readWizardFormFromDom();
        this.updateWizardSubmitState();
        return;
      }
      if (target.matches('[data-upload]')) {
        void this.handleMediaUpload(/** @type {HTMLInputElement} */ (target));
        return;
      }
      if (target.matches('[data-bind]')) {
        this.readWizardFormFromDom();
        this.markWizardDirty();
      }
    });

    this.bindUploadTileDragDrop(root);

    root.addEventListener('input', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.matches('[data-bind], [data-bind-category]')) return;
      this.readWizardFormFromDom();
      if (target.matches('[data-bind="creativeCredits"]')) {
        this.updateCharCounter('creativeCredits', /** @type {HTMLTextAreaElement} */ (target).value.length);
        if (this.wizard.step === 4) {
          this.updateWizardReview();
        }
      }
      this.markWizardDirty();
      this.scheduleDebouncedSave();
    });
  }

  async saveWizardIfDirty(showStatus = true) {
    const serialized = this.serializeWizardPayload();
    if (!this.wizard.dirty && serialized === this.wizard.lastSavedPayload) {
      return false;
    }
    await this.flushPendingSave(showStatus);
    return true;
  }

  async flushPendingSave(showStatus = true) {
    if (this._pendingSave) {
      await this._pendingSave;
      return;
    }
    const serialized = this.serializeWizardPayload();
    if (!this.wizard.dirty && serialized === this.wizard.lastSavedPayload) {
      return;
    }
    this._pendingSave = perfStep('saveWizard', () =>
      this.saveWizard({ showStatus, force: true }),
    ).finally(() => {
      this._pendingSave = null;
    });
    await this._pendingSave;
  }

  scheduleDebouncedSave() {
    if (!this._debouncedSave) {
      this._debouncedSave = debounce(() => {
        void this.saveWizardIfDirty(false).catch(() => {});
      }, SAVE_DEBOUNCE_MS);
    }
    this._debouncedSave();
  }

  async exitWizardToDashboard() {
    try {
      await this.flushPendingSave(false);
    } catch {
      // ignore save errors when leaving
    }
    this.view = 'dashboard';
    await this.loadDashboard();
    this.render();
  }

  async submitWizard() {
    this.readWizardFormFromDom();
    const issues = this.getWizardReviewIssues();
    if (issues.length) {
      this.showInlineError(
        '[data-wizard-error]',
        `Completa los campos obligatorios: ${issues.join(', ')}.`,
      );
      return;
    }
    const quotaPreview = this.getSubmitQuotaPreview();
    if (!quotaPreview.hasEnoughCredits) {
      this.showInlineError(
        '[data-wizard-error]',
        this.isQuotaPaymentUiEnabled()
          ? 'Para poder inscribir tu caso te falta pagar la inscripción. Puedes comprar tus inscripciones en el siguiente enlace o compartir el enlace de pago; una vez pagado se acredita automáticamente.'
          : 'No tienes inscripciones suficientes para enviar esta postulación. Puedes pagarlas desde el panel principal.',
      );
      this.updateWizardReview();
      return;
    }
    if (!this.wizard.form.acceptTerms || !this.wizard.form.acceptPrivacy) {
      this.showInlineError('[data-wizard-error]', LEGAL_ACCEPTANCE_ERROR_MESSAGE);
      this.updateWizardSubmitState();
      return;
    }
    try {
      await this.flushPendingSave(false);
      const wasObservedResubmission = this.isObservedResubmission();
      const payload = this.formToPayload(this.wizard.form, 'submit');
      const result = await perfStep('submitWizard', () =>
        this.api.submitSubmission(this.wizard.submissionId, payload),
      );
      this.view = 'dashboard';
      this.state.flash = wasObservedResubmission
        ? 'Postulación reenviada. Volverá a revisión del verificador.'
        : 'Postulación ingresada correctamente.';
      if (this.state.dashboard && result?.submission) {
        const submissions = this.state.dashboard.submissions ?? [];
        const existingIndex = submissions.findIndex((row) => row.id === result.submission.id);
        const row = {
          ...result.submission,
          statusLabel: result.submission.statusLabel ?? 'Ingresada',
        };
        if (existingIndex >= 0) {
          submissions[existingIndex] = { ...submissions[existingIndex], ...row };
        } else {
          submissions.unshift(row);
        }
        this.state.dashboard.submissions = submissions;
        this.render();
        void this.loadDashboard().then(() => this.render());
        return;
      }
      await this.loadDashboard();
      this.render();
    } catch (error) {
      this.showInlineError('[data-wizard-error]', error.message);
    }
  }

  async addWizardExternalLink() {
    this.readWizardFormFromDom();
    try {
      await this.api.updateSubmission(this.wizard.submissionId, {
        intent: 'add_external_link',
        externalUrl: this.wizard.form.externalUrl,
        linkLabel: this.wizard.form.linkLabel || 'Enlace externo',
      });
      await this.refreshWizardSubmission();
      this.updateMediaList();
      if (this.wizard.step === 4) this.updateWizardReview();
    } catch (error) {
      this.showInlineError('[data-wizard-error]', error.message);
    }
  }

  bindUploadTileDragDrop(root) {
    if (!this._uploadTileDragDepth) {
      this._uploadTileDragDepth = new WeakMap();
    }

    const setDragDepth = (tile, delta) => {
      const depth = Math.max(0, (this._uploadTileDragDepth.get(tile) ?? 0) + delta);
      this._uploadTileDragDepth.set(tile, depth);
      tile.classList.toggle('apap-postulacion__upload-tile--dragover', depth > 0);
    };

    root.addEventListener('dragenter', (event) => {
      const tile = event.target instanceof Element ? event.target.closest('.apap-postulacion__upload-tile') : null;
      if (!tile || !root.contains(tile)) return;
      event.preventDefault();
      setDragDepth(tile, 1);
    });

    root.addEventListener('dragleave', (event) => {
      const tile = event.target instanceof Element ? event.target.closest('.apap-postulacion__upload-tile') : null;
      if (!tile || !root.contains(tile)) return;
      setDragDepth(tile, -1);
    });

    root.addEventListener('dragover', (event) => {
      const tile = event.target instanceof Element ? event.target.closest('.apap-postulacion__upload-tile') : null;
      if (!tile || !root.contains(tile)) return;
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
    });

    root.addEventListener('drop', (event) => {
      const tile = event.target instanceof Element ? event.target.closest('.apap-postulacion__upload-tile') : null;
      if (!tile || !root.contains(tile)) return;
      event.preventDefault();
      this._uploadTileDragDepth.set(tile, 0);
      tile.classList.remove('apap-postulacion__upload-tile--dragover');
      const input = tile.querySelector('[data-upload]');
      const file = event.dataTransfer?.files?.[0];
      if (input instanceof HTMLInputElement && file) {
        void this.processMediaUpload(input, file);
      }
    });
  }

  readImageDimensions(file) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('No se pudo leer la imagen.'));
      };
      img.src = url;
    });
  }

  async validateMediaFile(file, mediaType) {
    const limits = MEDIA_UPLOAD_LIMITS[mediaType];
    if (!limits) return { ok: true };
    if (file.size > limits.maxBytes) {
      return { ok: false, message: `El archivo debe pesar menos de ${limits.label}.` };
    }
    if (limits.accept.length && file.type) {
      const mimeOk =
        limits.accept.includes(file.type) ||
        (mediaType === 'AUDIO' && file.type.startsWith('audio/'));
      if (!mimeOk) {
        return {
          ok: false,
          message:
            mediaType === 'VIDEO'
              ? 'Solo se permiten videos MP4 o MOV en H.264.'
              : 'Tipo de archivo no permitido para esta zona.',
        };
      }
    }
    if (mediaType === 'IMAGE' && limits.maxPixels) {
      try {
        const { width, height } = await this.readImageDimensions(file);
        if (width > limits.maxPixels || height > limits.maxPixels) {
          return {
            ok: false,
            message: `La imagen no puede superar ${limits.maxPixels}×${limits.maxPixels} píxeles.`,
          };
        }
      } catch {
        return { ok: false, message: 'No se pudo leer la imagen.' };
      }
    }
    if (mediaType === 'VIDEO') {
      try {
        await assertShopifyCompatibleVideoFile(file);
      } catch (error) {
        return {
          ok: false,
          message: error instanceof Error ? error.message : 'El formato del video no está permitido.',
        };
      }
    }
    return { ok: true };
  }

  setUploadTileProgress(tile, { uploading, progress = 0, fileName = '' } = {}) {
    if (!tile) return;
    tile.classList.toggle('apap-postulacion__upload-tile--uploading', !!uploading);
    const status = tile.querySelector('.apap-postulacion__upload-status');
    const bar = tile.querySelector('.apap-postulacion__upload-progress-bar');
    const label = tile.querySelector('.apap-postulacion__upload-status-label');
    const progressbar = tile.querySelector('.apap-postulacion__upload-progress');
    if (status) status.hidden = !uploading;
    const pct = Math.max(0, Math.min(100, progress));
    if (bar) bar.style.width = `${pct}%`;
    if (progressbar) progressbar.setAttribute('aria-valuenow', String(Math.round(pct)));
    if (label) {
      label.textContent =
        pct >= 100
          ? 'Finalizando…'
          : fileName
            ? `Subiendo ${fileName}… ${Math.round(pct)}%`
            : `Subiendo… ${Math.round(pct)}%`;
    }
  }

  resetUploadTile(tile, input) {
    if (tile) {
      this.setUploadTileProgress(tile, { uploading: false, progress: 0 });
      tile.classList.remove('apap-postulacion__upload-tile--dragover');
    }
    if (input) {
      input.disabled = false;
      input.value = '';
    }
  }

  async processMediaUpload(input, file) {
    const mediaType = input.getAttribute('data-upload');
    const tile = input.closest('.apap-postulacion__upload-tile');
    if (tile?.classList.contains('apap-postulacion__upload-tile--uploading')) return;

    const validation = await this.validateMediaFile(file, mediaType);
    if (!validation.ok) {
      this.showInlineError('[data-wizard-error]', validation.message);
      return;
    }

    input.disabled = true;
    this.setUploadTileProgress(tile, { uploading: true, progress: 0, fileName: file.name });

    try {
      await this.api.uploadFile(this.wizard.submissionId, mediaType, file, (progress) => {
        this.setUploadTileProgress(tile, { uploading: true, progress, fileName: file.name });
      });
      await this.refreshWizardSubmission();
      this.updateMediaList();
      if (this.wizard.step === 4) this.updateWizardReview();
    } catch (error) {
      this.showInlineError('[data-wizard-error]', error.message);
    } finally {
      this.resetUploadTile(tile, input);
    }
  }

  async handleMediaUpload(input) {
    const file = input.files?.[0];
    if (!file) return;
    await this.processMediaUpload(input, file);
  }

  async deleteWizardMedia(assetId) {
    if (!assetId) return;
    try {
      await this.api.updateSubmission(this.wizard.submissionId, {
        intent: 'delete_media',
        assetId,
      });
      await this.refreshWizardSubmission();
      this.updateMediaList();
      if (this.wizard.step === 4) this.updateWizardReview();
    } catch (error) {
      this.showInlineError('[data-wizard-error]', error.message);
    }
  }

  handleCategoryCheckboxChange(checkbox) {
    const maxCategories = this.getMaxCategories();
    const selected = this.querySelectorAll('[data-bind-category]:checked');
    if (checkbox.checked && selected.length > maxCategories) {
      checkbox.checked = false;
      return;
    }
    this.readWizardFormFromDom();
    this.syncPrimaryCategory();
    this.updateCategorySelectionUI();
    this.markWizardDirty();
  }

  readWizardFormFromDom() {
    this.wizard.form = { ...this.wizard.form, ...this.readWizardForm() };
  }

  readWizardForm() {
    const form = this.wizard.form;
    const categoryInputs = this.querySelectorAll('[data-bind-category]');
    const categoryIds = [];
    categoryInputs.forEach((input) => {
      if (/** @type {HTMLInputElement} */ (input).checked) {
        categoryIds.push(/** @type {HTMLInputElement} */ (input).value);
      }
    });
    const storedCategoryIds =
      categoryInputs.length > 0 ? categoryIds : (form.categoryIds ?? []);
    const read = (name) => this.querySelector(`[data-bind="${name}"]`)?.value ?? form[name] ?? '';
    const readLegalCheckbox = (name) => {
      const input = this.querySelector(`[data-bind-legal="${name}"]`);
      return input instanceof HTMLInputElement ? input.checked : Boolean(form[name]);
    };
    const nextForm = {
      ...form,
      categoryIds: storedCategoryIds,
      categoryId: storedCategoryIds[0] || '',
      campaignName: read('campaignName'),
      brand: read('brand'),
      productService: read('productService'),
      participantName: read('participantName'),
      agencyRazonSocial: read('agencyRazonSocial'),
      agencyRuc: read('agencyRuc'),
      agencyPhone: read('agencyPhone'),
      advertiser: read('advertiser'),
      clientEmail: read('clientEmail'),
      clientPhone: read('clientPhone'),
      creativeCredits: parseCreativeCreditsInput(read('creativeCredits')),
      externalUrl: read('externalUrl'),
      linkLabel: read('linkLabel'),
      acceptTerms: readLegalCheckbox('acceptTerms'),
      acceptPrivacy: readLegalCheckbox('acceptPrivacy'),
    };
    nextForm.caseType = resolveCaseTypeFromCategoryIds(
      nextForm.categoryIds,
      this.wizard.catalog?.categories ?? [],
    );
    return nextForm;
  }

  async saveWizard({ showStatus = true, force = false } = {}) {
    const serialized = this.serializeWizardPayload();
    if (!force && serialized === this.wizard.lastSavedPayload) {
      this.wizard.dirty = false;
      return { skipped: true };
    }
    const payload = this.formToPayload(this.wizard.form, 'autosave');
    const data = await this.api.updateSubmission(this.wizard.submissionId, payload);
    if (data.submission) this.wizard.submission = data.submission;
    this.wizard.lastSavedPayload = serialized;
    this.wizard.dirty = false;
    const status = this.querySelector('[data-wizard-status]');
    if (!status) return;
    if (showStatus) {
      status.textContent = data.savedAt
        ? `Guardado ${this.formatSavedAt(data.savedAt)}`
        : 'Guardado automáticamente';
    } else if (data.savedAt) {
      status.textContent = `Guardado ${this.formatSavedAt(data.savedAt)} · autoguardado cada minuto`;
    }
  }

  async refreshWizardSubmission() {
    const data = await this.api.getSubmission(this.wizard.submissionId);
    this.wizard.submission = data.submission;
    if (data.quota) {
      this.wizard.quota = data.quota;
    }
  }

  renderViewSubmission() {
    const s = this.state.viewSubmission;
    if (!s) return '';
    const editAction = s.editable
      ? `<button type="button" class="apap-postulacion__btn apap-postulacion__btn--ghost apap-postulacion__btn--sm" data-action="edit" data-id="${this.escapeAttr(s.id)}">Continuar edición</button>`
      : '';
    return `
      <div class="apap-postulacion__panel">
        ${this.renderImpersonationBanner()}
        <div class="apap-postulacion__toolbar">
          <div>
            <h2 class="apap-postulacion__toolbar-title">${this.escape(s.campaignName || 'Postulación')}</h2>
            <p class="apap-postulacion__item-meta">Ficha completa de la postulación</p>
          </div>
          <div class="apap-postulacion__nav">
            ${editAction}
            <button type="button" class="apap-postulacion__btn apap-postulacion__btn--primary" data-action="back-dashboard">Volver</button>
          </div>
        </div>
        <p><span class="apap-postulacion__badge ${this.getSubmissionStatusBadgeClass(s.status)}">${this.escape(s.statusLabel)}</span></p>
        ${
          s.verificationNote
            ? `<div class="apap-postulacion__alert apap-postulacion__alert--warning"><strong>Observación:</strong> ${this.escape(s.verificationNote)}</div>`
            : ''
        }
        ${this.renderSubmissionFicha(s)}
      </div>
    `;
  }

  bindViewActions() {
    this.bindCheckoutActions();
    this.querySelector('[data-action="edit"]')?.addEventListener('click', (event) => {
      const button = event.currentTarget;
      if (!(button instanceof HTMLButtonElement)) return;
      void this.withButtonLoading(button, () => this.openWizard(button.getAttribute('data-id')), 'Cargando…');
    });
    this.querySelector('[data-action="back-dashboard"]')?.addEventListener('click', async (event) => {
      const button = event.currentTarget;
      if (!(button instanceof HTMLButtonElement)) return;
      await this.withButtonLoading(button, async () => {
        this.view = 'dashboard';
        await this.loadDashboard();
        this.render();
      }, 'Cargando…');
    });
  }

  renderComprar() {
    const c = this.state.comprar;
    if (!c?.checkoutEnabled) {
      return `<div class="apap-postulacion__panel"><p>La compra de inscripciones no está disponible en este momento.</p><button type="button" class="apap-postulacion__btn apap-postulacion__btn--primary" data-action="back-dashboard">Volver</button></div>`;
    }
    return `
      <div class="apap-postulacion__panel">
        <div class="apap-postulacion__toolbar">
          <h2 class="h4">Comprar inscripciones</h2>
          <button type="button" class="apap-postulacion__btn apap-postulacion__btn--primary" data-action="back-dashboard">Volver</button>
        </div>
        <div class="apap-postulacion__packages">
          ${(c.packages ?? []).map((pkg) => `
            <div class="apap-postulacion__package">
              <strong>${this.escape(pkg.label)}</strong>
              <span>${this.escape(pkg.pieceCount)} pieza(s) · ${this.escape(pkg.priceAmount)} ${this.escape(pkg.priceCurrency)}</span>
              <button type="button" class="apap-postulacion__btn apap-postulacion__btn--primary" data-checkout="${this.escapeAttr(pkg.variantGid)}" data-qty="1">Ir al pago</button>
            </div>
          `).join('')}
        </div>
        <div data-comprar-error></div>
      </div>
    `;
  }

  bindComprarActions() {
    this.querySelector('[data-action="back-dashboard"]')?.addEventListener('click', async (event) => {
      const button = event.currentTarget;
      if (!(button instanceof HTMLButtonElement)) return;
      await this.withButtonLoading(button, async () => {
        this.view = 'dashboard';
        await this.loadDashboard();
        this.render();
      }, 'Cargando…');
    });
    this.querySelectorAll('[data-checkout]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        if (!(btn instanceof HTMLButtonElement)) return;
        await this.withButtonLoading(btn, async () => {
          const data = await this.api.comprarCheckout({
            variantGid: btn.getAttribute('data-checkout'),
            quantity: Number(btn.getAttribute('data-qty') || 1),
          });
          if (data.checkoutUrl) window.location.href = data.checkoutUrl;
        }, 'Redirigiendo…');
      });
    });
  }

  setButtonLoading(button, loading, loadingLabel = 'Cargando…') {
    if (!(button instanceof HTMLButtonElement)) return;
    if (loading) {
      if (!button.dataset.loadingActive) {
        button.dataset.originalHtml = button.innerHTML;
        button.dataset.loadingActive = 'true';
      }
      button.disabled = true;
      button.classList.add('apap-postulacion__btn--loading');
      button.setAttribute('aria-busy', 'true');
      button.replaceChildren();
      const spinner = document.createElement('span');
      spinner.className = 'apap-postulacion__btn-spinner';
      spinner.setAttribute('aria-hidden', 'true');
      const label = document.createElement('span');
      label.textContent = loadingLabel;
      button.append(spinner, label);
      return;
    }
    button.disabled = false;
    button.classList.remove('apap-postulacion__btn--loading');
    button.removeAttribute('aria-busy');
    if (button.dataset.loadingActive) {
      button.innerHTML = button.dataset.originalHtml ?? '';
      delete button.dataset.loadingActive;
      delete button.dataset.originalHtml;
    }
  }

  async withButtonLoading(button, task, loadingLabel = 'Cargando…') {
    this.setButtonLoading(button, true, loadingLabel);
    try {
      return await task();
    } catch (error) {
      if (error instanceof Error) {
        const selector =
          this.view === 'wizard'
            ? '[data-wizard-error]'
            : this.view === 'comprar'
              ? '[data-comprar-error]'
              : '[data-dashboard-error]';
        this.showInlineError(selector, error.message);
      }
      throw error;
    } finally {
      this.setButtonLoading(button, false);
    }
  }

  showInlineError(selector, message) {
    const el = this.querySelector(selector);
    if (el) el.innerHTML = `<div class="apap-postulacion__alert apap-postulacion__alert--error">${this.escape(message)}</div>`;
  }

  renderBackupLinkInline() {
    return this.showBackup ? `<p><a href="${this.escapeAttr(this.backupUrl)}" target="_blank" rel="noopener noreferrer">${this.escape(this.backupLabel)}</a></p>` : '';
  }

  escape(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  escapeAttr(value) {
    return this.escape(value).replace(/'/g, '&#39;');
  }
}

if (!customElements.get('apap-postulacion')) {
  customElements.define('apap-postulacion', ApapPostulacion);
}
